import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import {
    Box, Flex, Text, Button, Input, InputGroup, InputLeftElement, InputRightElement,
    Spinner, useToast, Badge, Textarea, Table, Thead, Tbody, Tr, Th, Td,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter, useDisclosure, VStack, HStack, Divider,
    IconButton, SimpleGrid, AlertDialog, AlertDialogOverlay, AlertDialogContent,
    AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Select,
    TableContainer, Icon, Collapse, Skeleton, SkeletonText, Tooltip,
    Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody,
    PopoverHeader, PopoverCloseButton, Image,
} from "@chakra-ui/react";
import {
    Search, ChevronLeft, CheckCircle, ClipboardList, History,
    Plus, X, ArrowUpDown, Filter, Package, AlertCircle, Check,
    ChevronDown, ChevronUp, MapPin, Minus, ArrowRightLeft, HelpCircle,
    AlertTriangle, CheckCheck, Clock, Trash2, CloudOff, Camera,
    Maximize2, ZoomIn, ZoomOut, ImageOff,
} from "lucide-react";
import {
    getRevisaoEstoquePosicoes,
    getRevisaoEstoqueItensPosicao,
    buscarProdutoRevisaoEstoque,
    salvarRevisaoEstoque,
    getRevisaoEstoqueHistorico,
    getRevisaoEstoqueUsuarios,
    getRevisaoEstoqueDetalhe,
    salvarItemRevisaoAndamento,
    removerItemRevisaoAndamento,
    salvarObservacaoRevisaoAndamento,
    descartarRevisaoAndamento,
} from "../services/api";
import { baseUrlApi } from "../config";
import { bloquearAtualizacaoAutomatica } from "../utils/atualizacaoAutomatica";
import { useSearchParams } from "react-router-dom";
import ProdutoImagem, { otimizarUrl } from "../components/ProdutoImagem";
import CorrigirImagemModal from "../components/despacho/CorrigirImagemModal";
import Preloader from "../components/Preloader";
import ScrollToTop from "../components/ScrollToTop";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const ACCENT = "#F68633";
const ACCENT_DARK = "#d97023";
const FOCUS_RING = "0 0 0 3px rgba(246,134,51,0.18)";

// Autosave da contagem: tempo de espera antes de mandar o item pro servidor.
// Curto de propósito — quem conta no celular atualiza a página sem avisar.
const DEBOUNCE_SALVAMENTO = 400;
const DEBOUNCE_OBSERVACAO = 800;

const primeiroNome = (nome) => (nome || "").trim().split(" ")[0] || "";

const CHAVE_CORRECAO_ABERTA = "revisaoEstoque:correcaoImagemAberta";
const RETOMAR_CORRECAO_MS = 15 * 60 * 1000;

const lerCorrecaoAberta = () => {
    try {
        const salvo = JSON.parse(window.sessionStorage.getItem(CHAVE_CORRECAO_ABERTA) || "null");
        return salvo && salvo.posicao && Date.now() - salvo.em < RETOMAR_CORRECAO_MS ? salvo : null;
    } catch { return null; }
};

const gravarCorrecaoAberta = (valor) => {
    try {
        if (valor) window.sessionStorage.setItem(CHAVE_CORRECAO_ABERTA, JSON.stringify(valor));
        else window.sessionStorage.removeItem(CHAVE_CORRECAO_ABERTA);
    } catch { /* sem sessionStorage a tela segue normal */ }
};

const dataHoraCurta = (valor) => {
    if (!valor) return null;
    const d = new Date(String(valor).replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const SeloImagemCorrigida = ({ em }) => {
    const quando = dataHoraCurta(em);
    return (
        <Tooltip label={quando ? `Foto real da peça publicada em ${quando}` : "A imagem oficial é foto real da peça"} hasArrow placement="top">
            <Badge colorScheme="green" variant="solid" fontSize="10px" px={2} py={0.5} borderRadius="md"
                display="inline-flex" alignItems="center" gap="4px" whiteSpace="nowrap"
                aria-label={quando ? `Imagem corrigida em ${quando}` : "Imagem corrigida"}>
                <CheckCircle size={11} /> Imagem corrigida
            </Badge>
        </Tooltip>
    );
};

const nomeDe = (p) => p?.nome_produto || p?.nome || `Produto ${p?.produto_id || ""}`;

const umaLinhaPorProduto = (lista) => {
    const porProduto = new Map();
    for (const item of lista) {
        const atual = porProduto.get(item.produto_id);
        porProduto.set(item.produto_id, atual
            ? { ...atual, quantidade_sistema: (atual.quantidade_sistema || 0) + (item.quantidade_sistema || 0) }
            : item);
    }
    return [...porProduto.values()];
};

const SAUDE_FAIXAS = {
    boa: { label: "Boa", cor: "#16A34A", bg: "green.50", texto: "green.700" },
    atencao: { label: "Atenção", cor: "#CA8A04", bg: "yellow.50", texto: "yellow.700" },
    critica: { label: "Crítica", cor: "#DC2626", bg: "red.50", texto: "red.700" },
    nunca: { label: "Nunca revisada", cor: "#9CA3AF", bg: "gray.100", texto: "gray.600" },
};

const faixaDe = (p) => SAUDE_FAIXAS[p?.faixa] || SAUDE_FAIXAS.nunca;

const textoSaude = (p) => {
    if (p?.saude === null || p?.saude === undefined) return "Nunca revisada";
    if (p.dias_desde_revisao === 0) return "Revisada hoje";
    if (p.dias_desde_revisao === 1) return "Revisada ontem";
    return `Revisada há ${p.dias_desde_revisao} dias`;
};

const LEGENDA_SAUDE = [
    { faixa: "boa", texto: "Revisada nos últimos 30 dias." },
    { faixa: "atencao", texto: "Sem revisão há 31 a 59 dias." },
    { faixa: "critica", texto: "Sem revisão há 60 dias ou mais." },
    { faixa: "nunca", texto: "Nenhuma revisão aplicada. Entra na média como 0%." },
];

const AjudaSaude = () => (
    <Popover placement="bottom-start" trigger="click">
        <PopoverTrigger>
            <IconButton aria-label="Como a saúde do estoque é calculada"
                icon={<HelpCircle size={13} />} size="xs" variant="ghost" colorScheme="gray"
                borderRadius="full" minW="18px" h="18px" color="gray.400" />
        </PopoverTrigger>
        <PopoverContent maxW="330px" boxShadow="lg" borderRadius="xl" _focus={{ outline: "none" }}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader fontSize="sm" fontWeight="bold" border="none" pb={1}>
                Como a saúde é calculada
            </PopoverHeader>
            <PopoverBody fontSize="xs" color="gray.600" pt={0} pb={4} lineHeight="1.6">
                <Text>
                    A posição vale <b>100%</b> no dia em que é revisada e perde pontos a cada dia
                    parada, chegando a <b>0% em 90 dias</b> sem revisão. Vale a última revisão
                    aplicada, mesmo que ela não tenha mudado nenhuma quantidade.
                </Text>
                <VStack align="stretch" spacing={1.5} mt={3}>
                    {LEGENDA_SAUDE.map(({ faixa, texto }) => (
                        <Flex key={faixa} align="flex-start" gap={2}>
                            <Box w="8px" h="8px" borderRadius="full" bg={SAUDE_FAIXAS[faixa].cor} mt="5px" flexShrink={0} />
                            <Text>
                                <b>{SAUDE_FAIXAS[faixa].label}:</b> {texto}
                            </Text>
                        </Flex>
                    ))}
                </VStack>
                <Text mt={3} color="gray.500">
                    O número grande é a média das posições com estoque. Clique numa faixa para
                    filtrar a lista.
                </Text>
            </PopoverBody>
        </PopoverContent>
    </Popover>
);

// Calcula status do item
const calcularStatus = (item) => {
    if (item.tipo_origem === "deslocado")      return "deslocado";
    if (item.tipo_origem === "nao_cadastrado") return "nao_cadastrado";
    const sistema = item.quantidade_sistema || 0;
    const contada = item.quantidade_contada ?? 0;
    // Item existente ainda não tocado: permanece como "não iniciado"
    if (!item._touched && contada === 0) return "nao_iniciado";
    if (contada === 0)       return "nao_encontrado";
    if (contada === sistema) return "conferido";
    return "divergencia";
};

const STATUS_CONFIG = {
    nao_iniciado:   { label: "Não iniciado",   cor: "gray",   icon: Clock,         emoji: "⏱" },
    conferido:      { label: "Conferido",      cor: "green",  icon: CheckCheck,    emoji: "✅" },
    divergencia:    { label: "Divergência",    cor: "yellow", icon: AlertTriangle, emoji: "⚠️" },
    nao_encontrado: { label: "Não encontrado", cor: "red",    icon: X,             emoji: "❌" },
    deslocado:      { label: "Deslocado",      cor: "purple", icon: ArrowRightLeft,emoji: "📦" },
    nao_cadastrado: { label: "Não cadastrado", cor: "orange", icon: HelpCircle,    emoji: "❓" },
};

// Indicador discreto do autosave da contagem
const ESTADO_SALVAMENTO = {
    salvando: { texto: "Salvando...", curto: "Salvando", cor: "gray.500", icone: null },
    salvo: { texto: "Salvo", curto: "Salvo", cor: "green.600", icone: Check },
    erro: { texto: "Sem conexão, tentando novamente", curto: "Sem conexão", cor: "red.500", icone: CloudOff },
};

const IndicadorSalvamento = ({ estado }) => {
    const cfg = ESTADO_SALVAMENTO[estado];
    if (!cfg) return null;
    return (
        <Flex align="center" gap={1} color={cfg.cor} flexShrink={0} whiteSpace="nowrap"
            fontSize="11px" fontWeight="medium" aria-live="polite">
            {cfg.icone ? <Icon as={cfg.icone} boxSize={3} /> : <Spinner size="xs" speed="0.7s" />}
            <Text display={{ base: "none", sm: "block" }}>{cfg.texto}</Text>
            <Text display={{ base: "block", sm: "none" }}>{cfg.curto}</Text>
        </Flex>
    );
};

const StatusBadge = ({ status, size = "sm" }) => {
    const cfg = STATUS_CONFIG[status];
    if (!cfg) return null;
    return (
        <Badge
            colorScheme={cfg.cor} fontSize={size === "xs" ? "9px" : "10px"}
            px={1.5} py={0.5} borderRadius="md" display="inline-flex" alignItems="center" gap={1}
            whiteSpace="nowrap"
        >
            <Icon as={cfg.icon} boxSize={size === "xs" ? 2.5 : 3} />
            {cfg.label}
        </Badge>
    );
};

// Stepper
const StepperRevisao = ({ current }) => {
    const steps = [
        { n: 1, label: "Posição" },
        { n: 2, label: "Contagem" },
        { n: 3, label: "Revisão" },
        { n: 4, label: "Aplicada" },
    ];
    return (
        <Flex align="center" justify="center" mb={5} px={{ base: 1, md: 4 }}>
            {steps.map((s, idx) => {
                const done = current > s.n;
                const active = current === s.n;
                const da = done || active;
                return (
                    <React.Fragment key={s.n}>
                        <Flex direction="column" align="center" gap={1.5} flex="0 0 auto" minW={{ base: "52px", md: "80px" }}>
                            <Flex
                                w="30px" h="30px" borderRadius="full" align="center" justify="center"
                                fontSize="xs" fontWeight="bold"
                                bg={done ? ACCENT : active ? "white" : "gray.100"}
                                color={done ? "white" : active ? ACCENT : "gray.400"}
                                border="2px solid" borderColor={da ? ACCENT : "gray.200"}
                                boxShadow={active ? `0 0 0 4px rgba(246,134,51,0.15)` : "none"}
                                transition="all 0.2s"
                            >
                                {done ? <Check size={14} strokeWidth={3} /> : s.n}
                            </Flex>
                            <Text fontSize="10px" fontWeight={active ? "bold" : "medium"}
                                color={da ? ACCENT : "gray.400"}
                                textTransform="uppercase" letterSpacing="wide">
                                {s.label}
                            </Text>
                        </Flex>
                        {idx < steps.length - 1 && (
                            <Box flex="1" h="2px" maxW="60px" mt="-18px"
                                bg={current > s.n ? ACCENT : "gray.200"}
                                transition="all 0.3s" borderRadius="full" />
                        )}
                    </React.Fragment>
                );
            })}
        </Flex>
    );
};

const MiniaturaProduto = ({ item, compact, onAmpliar }) => {
    const imagem = (
        <ProdutoImagem
            produto={item.produto_estoque}
            origem={null}
            width={compact ? "44px" : "56px"}
            height={compact ? "44px" : "56px"}
            imagemAPI={item.imagem_url || ""}
        />
    );
    if (!onAmpliar) return <Box flexShrink={0}>{imagem}</Box>;
    return (
        <Box as="button" type="button" onClick={onAmpliar} flexShrink={0} position="relative"
            borderRadius="md" cursor="zoom-in" aria-label={`Ampliar imagem de ${nomeDe(item)}`}
            transition="transform 0.15s ease"
            _hover={{ transform: "scale(1.04)" }} _active={{ transform: "scale(0.96)" }}
            _focusVisible={{ outline: "none", boxShadow: FOCUS_RING }}>
            {imagem}
            <Flex position="absolute" right="-5px" bottom="-5px" w="20px" h="20px"
                align="center" justify="center" borderRadius="full" pointerEvents="none"
                bg="white" color="gray.600" border="1px solid" borderColor="gray.200" boxShadow="sm">
                <Maximize2 size={10} strokeWidth={2.5} />
            </Flex>
        </Box>
    );
};

// Card de informação do produto (imagem + campos, estilo Despacho)
const ProdutoInfo = ({ item, compact = false, onAmpliar }) => (
    <HStack align="flex-start" spacing={3} flex="1" minW={0}>
        <MiniaturaProduto item={item} compact={compact} onAmpliar={onAmpliar} />
        <VStack align="stretch" spacing={1} flex="1" minW={0}>
            <Text fontSize="sm" fontWeight="bold" color="gray.800"
                bg="gray.100" px={2} py={1} borderRadius="md"
                noOfLines={2} lineHeight={1.25}>
                {nomeDe(item)}
            </Text>
            <Flex wrap="wrap" columnGap={2} rowGap={0.5}>
                {item.produto_id && <Text fontSize="11px" color="gray.500" whiteSpace="nowrap"><Text as="span" fontWeight="bold" color="gray.600">SKU:</Text> {item.produto_id}</Text>}
                {item.codigo_global && <Text fontSize="11px" color="gray.500" whiteSpace="nowrap"><Text as="span" fontWeight="bold" color="gray.600">Global:</Text> {item.codigo_global}</Text>}
                {item.codigo_fornecedor && <Text fontSize="11px" color="gray.500" whiteSpace="nowrap"><Text as="span" fontWeight="bold" color="gray.600">Forn:</Text> {item.codigo_fornecedor}</Text>}
                {item.codigo_fabricante && <Text fontSize="11px" color="gray.500" whiteSpace="nowrap"><Text as="span" fontWeight="bold" color="gray.600">Fabr:</Text> {item.codigo_fabricante}</Text>}
                {item.nome_fabricante && <Text fontSize="11px" color="gray.500" overflowWrap="anywhere"><Text as="span" fontWeight="bold" color="gray.600">Marca:</Text> {item.nome_fabricante}</Text>}
                {item.codigo_barra && <Text fontSize="11px" color="gray.500" whiteSpace="nowrap"><Text as="span" fontWeight="bold" color="gray.600">Cód. Barra:</Text> {item.codigo_barra}</Text>}
                {item.fornecedor_nome && <Text fontSize="11px" color="gray.500" overflowWrap="anywhere"><Text as="span" fontWeight="bold" color="gray.600">Fornecedor:</Text> {item.fornecedor_nome}</Text>}
            </Flex>
        </VStack>
    </HStack>
);

const ZOOM_IMAGEM = 2.5;

const ConteudoImagemAmpliada = ({ item, onFechar, onCorrigir }) => {
    const original = item.imagem_url || "";
    const [src, setSrc] = useState(() => (original ? otimizarUrl(original, 1920) : ""));
    const [estado, setEstado] = useState(original ? "carregando" : "vazio");
    const [zoom, setZoom] = useState(false);
    const areaRef = useRef(null);
    const alvoRef = useRef({ x: 0.5, y: 0.5 });

    useLayoutEffect(() => {
        const area = areaRef.current;
        if (!area) return;
        const { x, y } = alvoRef.current;
        area.scrollLeft = zoom ? x * area.scrollWidth - area.clientWidth / 2 : 0;
        area.scrollTop = zoom ? y * area.scrollHeight - area.clientHeight / 2 : 0;
    }, [zoom]);

    const falhou = () => {
        if (src !== original) setSrc(original);
        else setEstado("erro");
    };

    const ampliarNoPonto = (e) => {
        if (estado !== "ok") return;
        if (!zoom) {
            const r = e.currentTarget.getBoundingClientRect();
            alvoRef.current = {
                x: r.width ? (e.clientX - r.left) / r.width : 0.5,
                y: r.height ? (e.clientY - r.top) / r.height : 0.5,
            };
        }
        setZoom(!zoom);
    };

    const alternarZoom = () => {
        alvoRef.current = { x: 0.5, y: 0.5 };
        setZoom((z) => !z);
    };

    return (
        <Flex direction="column" h="100%" minH={0}>
            <Flex align="flex-start" gap={3} px={{ base: 4, md: 6 }} pb={3} flexShrink={0}
                borderBottom="1px solid" borderColor="gray.100"
                sx={{ paddingTop: "calc(12px + env(safe-area-inset-top))" }}>
                <Box flex="1" minW={0}>
                    <ModalHeader p={0} fontSize={{ base: "sm", md: "md" }} fontWeight="bold"
                        color="gray.800" lineHeight={1.3} noOfLines={2}
                        sx={{ "@media (max-height: 480px)": { WebkitLineClamp: 1 } }}>
                        {nomeDe(item)}
                    </ModalHeader>
                    <Flex mt={1} wrap="wrap" align="center" columnGap={2} rowGap={1}>
                        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap"><Text as="span" fontWeight="bold" color="gray.600">SKU:</Text> {item.produto_id}</Text>
                        {item.codigo_global && <Text fontSize="xs" color="gray.500" overflowWrap="anywhere"><Text as="span" fontWeight="bold" color="gray.600">Global:</Text> {item.codigo_global}</Text>}
                        {item.foto_real && <SeloImagemCorrigida em={item.foto_real_em} />}
                    </Flex>
                </Box>
                <IconButton icon={<X size={20} />} aria-label="Fechar imagem" onClick={onFechar}
                    variant="ghost" borderRadius="full" w="40px" h="40px" minW="40px" mt="-6px" mr="-8px"
                    color="gray.600" _hover={{ bg: "gray.100", color: "gray.800" }} />
            </Flex>

            <Box ref={areaRef} flex="1" minH={0} position="relative" bg="white"
                overflow={zoom ? "auto" : "hidden"} data-testid="area-imagem-ampliada">
                {estado === "carregando" && (
                    <Flex position="absolute" inset={0} align="center" justify="center">
                        <Spinner color={ACCENT} thickness="3px" size="lg" />
                    </Flex>
                )}
                {(estado === "vazio" || estado === "erro") && (
                    <Flex position="absolute" inset={0} direction="column" align="center" justify="center" gap={3} px={6} textAlign="center">
                        <Flex w="64px" h="64px" borderRadius="full" bg="gray.100" color="gray.400" align="center" justify="center">
                            <ImageOff size={28} />
                        </Flex>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                            {estado === "vazio" ? "Produto sem imagem" : "Não foi possível carregar a imagem"}
                        </Text>
                    </Flex>
                )}
                {src && estado !== "erro" && (
                    <Image
                        src={src}
                        alt={`Imagem de ${nomeDe(item)}`}
                        position="absolute"
                        top={zoom ? 0 : { base: 3, md: 6 }}
                        left={zoom ? 0 : { base: 3, md: 6 }}
                        w={zoom ? `${ZOOM_IMAGEM * 100}%` : { base: "calc(100% - 24px)", md: "calc(100% - 48px)" }}
                        h={zoom ? `${ZOOM_IMAGEM * 100}%` : { base: "calc(100% - 24px)", md: "calc(100% - 48px)" }}
                        maxW="none"
                        objectFit="contain"
                        cursor={zoom ? "zoom-out" : "zoom-in"}
                        opacity={estado === "ok" ? 1 : 0}
                        transition="opacity 0.2s ease"
                        draggable={false}
                        userSelect="none"
                        onLoad={() => setEstado("ok")}
                        onError={falhou}
                        onClick={ampliarNoPonto}
                    />
                )}
            </Box>

            <Flex gap={3} px={{ base: 4, md: 6 }} pt={3} flexShrink={0} justify={{ md: "center" }}
                borderTop="1px solid" borderColor="gray.100"
                sx={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
                <IconButton icon={zoom ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
                    aria-label={zoom ? "Afastar imagem" : "Aproximar imagem"}
                    variant="outline" borderColor="gray.300" color="gray.700"
                    w="44px" h="44px" minW="44px" borderRadius="lg"
                    isDisabled={estado !== "ok"} onClick={alternarZoom} />
                <Button colorScheme="purple" leftIcon={<Camera size={16} />}
                    h="44px" borderRadius="lg" fontSize="sm" fontWeight="semibold"
                    flex={{ base: 1, md: "0 0 auto" }} px={{ md: 10 }}
                    onClick={onCorrigir}>
                    Corrigir imagem
                </Button>
            </Flex>
        </Flex>
    );
};

const ImagemAmpliada = ({ item, isOpen, onFechar, onCorrigir }) => (
    <Modal isOpen={isOpen && !!item} onClose={onFechar} size="full" allowPinchZoom>
        <ModalOverlay />
        <ModalContent m={0} borderRadius={0} bg="white" overflow="hidden"
            sx={{ height: "100vh", "@supports (height: 100dvh)": { height: "100dvh" } }}>
            {item && <ConteudoImagemAmpliada item={item} onFechar={onFechar} onCorrigir={onCorrigir} />}
        </ModalContent>
    </Modal>
);

// Input numérico com +/-
const InputQtd = ({ value, onChange, borderColor, ariaLabel }) => {
    const num = parseInt(value, 10);
    const atual = isNaN(num) ? 0 : Math.max(0, num);
    const inc = () => onChange(String(atual + 1));
    const dec = () => onChange(String(Math.max(0, atual - 1)));

    return (
        <HStack spacing={0}>
            <IconButton
                icon={<Minus size={12} />} size="xs" variant="outline"
                borderRightRadius={0} borderRight="0"
                onClick={dec} isDisabled={atual <= 0}
                aria-label="Diminuir"
                borderColor="gray.300" color="gray.600"
                _hover={{ bg: "gray.100" }}
            />
            <Input
                type="number" inputMode="numeric" min={0}
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || /^\d+$/.test(v)) onChange(v);
                }}
                onFocus={(e) => e.target.select()}
                size="xs" textAlign="center" fontWeight="semibold"
                bg="white" borderRadius={0}
                borderColor={borderColor || "gray.300"}
                _hover={{ borderColor: "gray.400" }}
                _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING, zIndex: 1 }}
                w="54px" h="28px"
                aria-label={ariaLabel}
            />
            <IconButton
                icon={<Plus size={12} />} size="xs" variant="outline"
                borderLeftRadius={0} borderLeft="0"
                onClick={inc}
                aria-label="Aumentar"
                borderColor="gray.300" color="gray.600"
                _hover={{ bg: "gray.100" }}
            />
        </HStack>
    );
};

const RevisaoEstoque = () => {
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState("nova");
    const [step, setStep] = useState(1);

    // Step 1
    const [posicoes, setPosicoes] = useState([]);
    const [loadingPosicoes, setLoadingPosicoes] = useState(false);
    const [filtroPosicao, setFiltroPosicao] = useState("");
    const [ordPosicao, setOrdPosicao] = useState("localizacao_asc");
    const [filtroFaixa, setFiltroFaixa] = useState("");
    const [resumoSaude, setResumoSaude] = useState(null);
    const [posicaoSelecionada, setPosicaoSelecionada] = useState("");

    // Step 2
    const [itens, setItens] = useState([]);
    const [loadingItens, setLoadingItens] = useState(false);
    const [filtroItem, setFiltroItem] = useState("");
    const [ordItem, setOrdItem] = useState("ordem");
    const [buscaProduto, setBuscaProduto] = useState("");
    const [resultadosBusca, setResultadosBusca] = useState([]);
    const [loadingBusca, setLoadingBusca] = useState(false);
    const buscaTimeoutRef = useRef(null);

    // Conferência em andamento (estado salvo no servidor) + autosave
    const [andamento, setAndamento] = useState(null);
    const [estadoSalvamento, setEstadoSalvamento] = useState(null); // null | salvando | salvo | erro
    const posicaoRef = useRef("");        // posição que está sendo contada agora
    const itensRef = useRef([]);          // espelho de `itens` para montar o payload sem depender do render
    const stepRef = useRef(1);
    const pendentesRef = useRef(new Map()); // produto_id -> payload (o último vence)
    const flushTimerRef = useRef(null);
    const flushRef = useRef(null);
    const enviandoRef = useRef(false);
    const tentativasRef = useRef(0);
    const obsTimerRef = useRef(null);
    const guardaVoltarRef = useRef(0);    // entradas de histórico criadas para segurar o botão voltar
    const ignorarPopRef = useRef(false);

    const [itemCorrigirImagem, setItemCorrigirImagem] = useState(null);
    const corrigirImagemAbertoRef = useRef(false);
    const [imagemAmpliada, setImagemAmpliada] = useState({ aberta: false, produtoId: null });
    const imagemAmpliadaAbertaRef = useRef(false);
    const usuarioId = useMemo(() => {
        try { return JSON.parse(window.localStorage.getItem("user") || "null")?.id; } catch { return undefined; }
    }, []);

    // Modal de adicionar produto externo
    const [produtoParaAdicionar, setProdutoParaAdicionar] = useState(null);
    const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();

    // Step 3
    const [observacao, setObservacao] = useState("");
    const [filtroStatusRev, setFiltroStatusRev] = useState("todos");
    const [salvando, setSalvando] = useState(false);
    const [revisaoSalvaId, setRevisaoSalvaId] = useState(null);

    // Histórico
    const [historico, setHistorico] = useState([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);
    const [paginaHistorico, setPaginaHistorico] = useState(1);
    const [ultimaPaginaHistorico, setUltimaPaginaHistorico] = useState(1);
    const [filtroDataInicio, setFiltroDataInicio] = useState("");
    const [filtroDataFim, setFiltroDataFim] = useState("");
    const [filtroHistPosicao, setFiltroHistPosicao] = useState("");
    const [filtroHistUserId, setFiltroHistUserId] = useState("");
    const [ordHistorico, setOrdHistorico] = useState("applied_at_desc");
    const [usuariosDisponiveis, setUsuariosDisponiveis] = useState([]);
    const [filtrosAbertos, setFiltrosAbertos] = useState(false);

    // Dialogs
    const { isOpen: isDetalheOpen, onOpen: onDetalheOpen, onClose: onDetalheClose } = useDisclosure();
    const [detalheRevisao, setDetalheRevisao] = useState(null);
    const [loadingDetalhe, setLoadingDetalhe] = useState(false);

    const confirmRemoverRef = useRef();
    const { isOpen: isRemOpen, onOpen: onRemOpen, onClose: onRemClose } = useDisclosure();
    const [itemARemover, setItemARemover] = useState(null);

    const confirmAplicarRef = useRef();
    const { isOpen: isAplOpen, onOpen: onAplOpen, onClose: onAplClose } = useDisclosure();

    const confirmVoltarRef = useRef();
    const { isOpen: isVoltarOpen, onOpen: onVoltarOpen, onClose: onVoltarClose } = useDisclosure();

    const confirmDescartarRef = useRef();
    const { isOpen: isDescOpen, onOpen: onDescOpen, onClose: onDescClose } = useDisclosure();
    const [posicaoParaDescartar, setPosicaoParaDescartar] = useState(null);
    const [descartando, setDescartando] = useState(false);

    // Effects
    useEffect(() => {
        if (activeTab === "nova" && step === 1) carregarPosicoes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, ordPosicao, filtroFaixa, step]);

    useEffect(() => {
        if (activeTab === "historico") {
            carregarHistorico(1);
            carregarUsuariosHistorico();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, ordHistorico]);

    useEffect(() => {
        return () => { if (buscaTimeoutRef.current) clearTimeout(buscaTimeoutRef.current); };
    }, []);

    useEffect(() => { itensRef.current = itens; }, [itens]);
    useEffect(() => { stepRef.current = step; }, [step]);

    // Enquanto o operador está contando, o detector de nova versão não recarrega
    // a página sozinho. O banner continua aparecendo e o botão "Atualizar agora"
    // continua funcionando — agora sem custo, porque a contagem está salva.
    useEffect(() => {
        if (activeTab !== "nova" || (step !== 2 && step !== 3)) return undefined;
        return bloquearAtualizacaoAutomatica();
    }, [activeTab, step]);

    // Botão voltar do celular: uma entrada de histórico segura a saída da tela.
    // Ao voltar, o fluxo recua uma etapa (3 -> 2) ou pergunta antes de sair (2).
    // A entrada não é desfeita no unmount de propósito: mexer no histórico nessa
    // hora atrapalharia uma navegação legítima do usuário pelo menu.
    useEffect(() => {
        const emContagem = activeTab === "nova" && (step === 2 || step === 3);
        if (!emContagem) return undefined;

        const empilhar = () => {
            window.history.pushState({ revisaoEstoque: true }, "");
            guardaVoltarRef.current += 1;
        };
        // Uma guarda por passagem pela contagem — trocar de etapa não empilha outra.
        if (guardaVoltarRef.current === 0) empilhar();

        const aoVoltar = () => {
            if (ignorarPopRef.current) return;
            guardaVoltarRef.current = Math.max(0, guardaVoltarRef.current - 1);
            empilhar(); // mantém a guarda de pé para o próximo toque
            if (corrigirImagemAbertoRef.current) { fecharCorrigirImagem(); return; }
            if (imagemAmpliadaAbertaRef.current) { fecharImagemAmpliada(); return; }
            if (stepRef.current === 3) setStep(2);
            else if (stepRef.current === 2) onVoltarOpen();
        };

        window.addEventListener("popstate", aoVoltar);
        return () => window.removeEventListener("popstate", aoVoltar);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, step]);

    // Rede de segurança para F5 / fechar a aba antes do debounce: a requisição
    // com keepalive sobrevive à saída da página. Se falhar, perde-se no máximo o
    // último valor digitado — todo o resto já está gravado.
    useEffect(() => {
        const flushDeSaida = () => {
            const posicao = posicaoRef.current;
            if (!posicao || pendentesRef.current.size === 0) return;
            const token = window.localStorage.getItem("token");
            const base = `${baseUrlApi}/revisao-estoque/andamento/${encodeURIComponent(posicao)}`;
            pendentesRef.current.forEach((payload) => {
                try {
                    fetch(payload.__remover ? `${base}/item/${payload.produto_id}` : `${base}/item`, {
                        method: payload.__remover ? "DELETE" : "PUT",
                        keepalive: true,
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested": "XMLHttpRequest",
                            Authorization: `Bearer ${token}`,
                        },
                        body: payload.__remover ? undefined : JSON.stringify(payload),
                    });
                } catch { /* melhor esforço */ }
            });
        };
        const aoEsconder = () => { if (document.visibilityState === "hidden") flushDeSaida(); };

        window.addEventListener("pagehide", flushDeSaida);
        document.addEventListener("visibilitychange", aoEsconder);
        return () => {
            window.removeEventListener("pagehide", flushDeSaida);
            document.removeEventListener("visibilitychange", aoEsconder);
        };
    }, []);

    useEffect(() => {
        const alvo = searchParams.get("posicao");
        if (alvo) {
            setSearchParams({}, { replace: true });
            setActiveTab("nova");
            selecionarPosicao(alvo);
            return;
        }
        // O celular pode recarregar a aba ao abrir a câmera: volta para a
        // mesma posição, com a contagem que já estava salva no servidor.
        const correcao = lerCorrecaoAberta();
        gravarCorrecaoAberta(null);
        if (!correcao) return;
        setActiveTab("nova");
        selecionarPosicao(correcao.posicao).then(() => {
            toast({
                title: "Correção de imagem interrompida",
                description: "A página recarregou antes de publicar as fotos. A contagem está salva: abra Corrigir imagem de novo no item.",
                status: "info",
                duration: 7000,
                isClosable: true,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ═══ Funções ═══
    const carregarPosicoes = useCallback(async () => {
        setLoadingPosicoes(true);
        try {
            const li = ordPosicao.lastIndexOf("_");
            const res = await getRevisaoEstoquePosicoes({
                order_by: ordPosicao.slice(0, li),
                order_dir: ordPosicao.slice(li + 1),
                ...(filtroFaixa ? { faixa: filtroFaixa } : {}),
            });
            setPosicoes(res.data.data || []);
            setResumoSaude(res.data.resumo || null);
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar posições", status: "error", duration: 3000 });
        } finally {
            setLoadingPosicoes(false);
        }
    }, [ordPosicao, filtroFaixa, toast]);

    const selecionarPosicao = async (posicao) => {
        await flushAgora();          // nada pendente da posição anterior
        limparFilaSalvamento();
        posicaoRef.current = posicao;
        setPosicaoSelecionada(posicao);
        setItens([]);
        setAndamento(null);
        setBuscaProduto("");
        setResultadosBusca([]);
        setFiltroItem("");
        setOrdItem("ordem");
        setLoadingItens(true);
        setStep(2);
        try {
            // A rota da posição já devolve a conferência em andamento quando existe.
            const res = await getRevisaoEstoqueItensPosicao(posicao);
            const meta = res.data.andamento || null;
            const data = umaLinhaPorProduto(res.data.data || []).map((item, idx) => ({
                ...item,
                quantidade_contada_str: String(item.quantidade_contada ?? 0),
                tipo_origem: item.tipo_origem || "existente",
                acao: item.acao || "ajustar",
                _touched: !!item.contado,
                _ordem_original: item.ordem ?? idx,
            }));
            setItens(data);
            setAndamento(meta);
            setObservacao(meta?.observacao || "");
            if (meta) {
                setEstadoSalvamento("salvo");
                toast({
                    title: "Revisão retomada",
                    description: `${meta.total_contados} de ${meta.total_itens} ${meta.total_itens === 1 ? "item já contado" : "itens já contados"}.`,
                    status: "info",
                    duration: 3500,
                });
            }
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar itens", status: "error", duration: 3000 });
        } finally {
            setLoadingItens(false);
        }
    };

    const abrirCorrigirImagem = (item) => {
        corrigirImagemAbertoRef.current = true;
        gravarCorrecaoAberta({ posicao: posicaoRef.current, produto_id: item.produto_id, em: Date.now() });
        setItemCorrigirImagem({
            id: item.produto_id,
            uniqueId: `revisao-${posicaoRef.current}-${item.produto_id}`,
            name: nomeDe(item),
            globalCode: item.codigo_global,
            brand: item.nome_fabricante,
            revisaoPosicao: posicaoRef.current,
        });
    };

    const fecharCorrigirImagem = () => {
        corrigirImagemAbertoRef.current = false;
        gravarCorrecaoAberta(null);
        setItemCorrigirImagem(null);
    };

    const abrirImagemAmpliada = (item) => {
        toast.closeAll();
        imagemAmpliadaAbertaRef.current = true;
        setImagemAmpliada({ aberta: true, produtoId: item.produto_id });
    };

    const fecharImagemAmpliada = () => {
        imagemAmpliadaAbertaRef.current = false;
        setImagemAmpliada((atual) => ({ ...atual, aberta: false }));
    };

    const itemAmpliado = imagemAmpliada.produtoId == null ? null
        : itens.find((i) => i.produto_id === imagemAmpliada.produtoId) || null;

    const corrigirImagemAmpliada = () => {
        const item = itemAmpliado;
        fecharImagemAmpliada();
        if (item) abrirCorrigirImagem(item);
    };

    // Só o selo e a foto do item mudam: a contagem e a fila de autosave não são tocadas.
    const imagemCorrigida = (produto, res) => {
        setItens((prev) => prev.map((i) => (i.produto_id === produto.id
            ? { ...i, foto_real: true, foto_real_em: new Date().toISOString(), imagem_url: res?.imagem_url || i.imagem_url }
            : i)));
    };

    const qtdContadaDe = (item) => {
        const num = parseInt(item.quantidade_contada_str, 10);
        return isNaN(num) || num < 0 ? 0 : num;
    };

    // ═══ Autosave da contagem ═══
    // Cada item alterado entra numa fila (uma entrada por produto, a última
    // vence) e é gravado no servidor. Nada disso altera estoque: é só o estado
    // da conferência, para o operador poder atualizar a página, ser interrompido
    // ou trocar de aparelho sem recomeçar do zero.

    const payloadDoItem = (item) => ({
        produto_id: item.produto_id,
        quantidade_sistema: item.quantidade_sistema || 0,
        quantidade_contada: qtdContadaDe(item),
        contado: !!item._touched,
        tipo_origem: item.tipo_origem || "existente",
        acao: item.acao || "ajustar",
        posicoes_origem: (item.posicoes_existentes || []).length
            ? item.posicoes_existentes
            : (item.posicoes_origem || null),
        ordem: item._ordem_original ?? 0,
    });

    const agendarFlush = useCallback((ms) => {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(() => {
            flushTimerRef.current = null;
            if (flushRef.current) flushRef.current();
        }, ms);
    }, []);

    const flush = useCallback(async () => {
        if (enviandoRef.current) return;
        const posicao = posicaoRef.current;
        if (!posicao || pendentesRef.current.size === 0) return;

        enviandoRef.current = true;
        let falhou = false;
        try {
            for (const payload of [...pendentesRef.current.values()]) {
                try {
                    if (payload.__remover) await removerItemRevisaoAndamento(posicao, payload.produto_id);
                    else await salvarItemRevisaoAndamento(posicao, payload);
                } catch (e) {
                    console.error("Falha ao salvar a contagem", e);
                    falhou = true;
                    break;
                }
                // Só sai da fila se o operador não mexeu no item de novo nesse meio tempo.
                if (pendentesRef.current.get(payload.produto_id) === payload) {
                    pendentesRef.current.delete(payload.produto_id);
                }
            }
        } finally {
            enviandoRef.current = false;
        }

        if (falhou) {
            tentativasRef.current += 1;
            setEstadoSalvamento("erro");
            agendarFlush(Math.min(15000, 1000 * 2 ** tentativasRef.current));
            return;
        }

        tentativasRef.current = 0;
        if (pendentesRef.current.size > 0) { agendarFlush(200); return; }
        setEstadoSalvamento("salvo");
    }, [agendarFlush]);

    useEffect(() => { flushRef.current = flush; }, [flush]);

    const enfileirar = useCallback((payload) => {
        if (!posicaoRef.current) return;
        pendentesRef.current.set(payload.produto_id, payload);
        setEstadoSalvamento("salvando");
        agendarFlush(DEBOUNCE_SALVAMENTO);
    }, [agendarFlush]);

    const salvarItem = (item) => enfileirar(payloadDoItem(item));
    const marcarItemRemovido = (produtoId) => enfileirar({ produto_id: produtoId, __remover: true });

    /** Grava agora o que estiver pendente (trocar de etapa, sair, aplicar). */
    const flushAgora = useCallback(async () => {
        if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
        if (obsTimerRef.current) { clearTimeout(obsTimerRef.current); obsTimerRef.current = null; }
        for (let i = 0; i < 40 && enviandoRef.current; i++) {
            await new Promise((r) => setTimeout(r, 50));
        }
        if (flushRef.current) await flushRef.current();
    }, []);

    const limparFilaSalvamento = useCallback(() => {
        if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
        if (obsTimerRef.current) { clearTimeout(obsTimerRef.current); obsTimerRef.current = null; }
        pendentesRef.current.clear();
        tentativasRef.current = 0;
        setEstadoSalvamento(null);
    }, []);

    const atualizarContagem = (produtoId, valor) => {
        setItens((prev) =>
            prev.map((i) =>
                i.produto_id === produtoId ? { ...i, quantidade_contada_str: valor, _touched: true } : i
            )
        );
        // Campo vazio é transitório (apagou para digitar outro número): não grava,
        // senão viraria "0 contado" e a revisão zeraria o item sem o operador querer.
        if (valor === "") return;
        const base = itensRef.current.find((i) => i.produto_id === produtoId);
        if (base) salvarItem({ ...base, quantidade_contada_str: valor, _touched: true });
    };

    const atualizarObservacao = (valor) => {
        setObservacao(valor);
        if (!posicaoRef.current) return;
        if (obsTimerRef.current) clearTimeout(obsTimerRef.current);
        setEstadoSalvamento("salvando");
        obsTimerRef.current = setTimeout(async () => {
            obsTimerRef.current = null;
            try {
                await salvarObservacaoRevisaoAndamento(posicaoRef.current, valor || null);
                if (pendentesRef.current.size === 0) setEstadoSalvamento("salvo");
            } catch (e) {
                console.error("Falha ao salvar a observação", e);
                setEstadoSalvamento("erro");
            }
        }, DEBOUNCE_OBSERVACAO);
    };

    const itensComContagem = useMemo(() => {
        return itens.map((i) => {
            const qtd = qtdContadaDe(i);
            const withQtd = { ...i, quantidade_contada: qtd };
            return { ...withQtd, status: calcularStatus(withQtd) };
        });
    }, [itens]);

    const abrirConfirmRemover = (item) => { setItemARemover(item); onRemOpen(); };
    const removerItemConfirmado = () => {
        if (itemARemover) {
            setItens((prev) => prev.filter((i) => i.produto_id !== itemARemover.produto_id));
            marcarItemRemovido(itemARemover.produto_id);
            toast({ title: "Item removido", status: "info", duration: 1500 });
        }
        setItemARemover(null);
        onRemClose();
    };

    const handleBuscaProduto = (val) => {
        setBuscaProduto(val);
        setResultadosBusca([]);
        if (buscaTimeoutRef.current) clearTimeout(buscaTimeoutRef.current);
        if (val.trim().length < 2) { setLoadingBusca(false); return; }
        setLoadingBusca(true);
        buscaTimeoutRef.current = setTimeout(async () => {
            try {
                const res = await buscarProdutoRevisaoEstoque(val.trim(), posicaoSelecionada);
                setResultadosBusca(res.data.data || []);
            } catch (e) {
                console.error(e);
                toast({ title: "Erro na busca", status: "error", duration: 2000 });
            } finally {
                setLoadingBusca(false);
            }
        }, 400);
    };

    // Clicar em produto da busca → decide fluxo
    const clicarProdutoBusca = (produto) => {
        const jaExiste = itens.some((i) => i.produto_id === produto.produto_id);
        if (jaExiste) {
            toast({ title: "Produto já está na lista", status: "warning", duration: 2000 });
            setBuscaProduto("");
            setResultadosBusca([]);
            return;
        }

        const temPosicaoSistema = produto.quantidade_sistema > 0;
        const temOutrasPosicoes = (produto.posicoes_existentes || []).length > 0;

        if (temPosicaoSistema) {
            // Já está cadastrado nesta posição no sistema (mas por algum motivo não veio na lista inicial) — adiciona normal
            adicionarProdutoSimples(produto, "existente", "ajustar");
            return;
        }

        if (temOutrasPosicoes) {
            // Item DESLOCADO — abre modal de ação
            setProdutoParaAdicionar({ ...produto, _kind: "deslocado" });
            onAddOpen();
            return;
        }

        // Item NÃO CADASTRADO em nenhuma posição
        setProdutoParaAdicionar({ ...produto, _kind: "nao_cadastrado" });
        onAddOpen();
    };

    const adicionarProdutoSimples = (produto, tipo_origem, acao) => {
        const novo = {
            ...produto,
            quantidade_contada_str: "0",
            tipo_origem,
            acao,
            _ordem_original: itensRef.current.length,
        };
        setItens((prev) => [...prev, novo]);
        salvarItem(novo);
        toast({ title: "Produto adicionado", status: "success", duration: 1500 });
        setBuscaProduto("");
        setResultadosBusca([]);
    };

    const confirmarAdicao = (acao) => {
        if (!produtoParaAdicionar) return;
        const p = produtoParaAdicionar;
        const tipo = p._kind;
        const novo = {
            ...p,
            quantidade_contada_str: "0",
            quantidade_sistema: 0,
            tipo_origem: tipo,
            acao,
            _ordem_original: itensRef.current.length,
        };
        setItens((prev) => [...prev, novo]);
        salvarItem(novo);
        const labelAcao = {
            transferir_fisico: "marcado p/ transferência física",
            atualizar_sistema: "marcado p/ atualizar sistema",
            cadastrar: "será cadastrado nesta posição",
            revisao_manual: "marcado p/ revisão manual",
        }[acao] || "adicionado";
        toast({ title: `Produto ${labelAcao}`, status: "success", duration: 2500 });
        setProdutoParaAdicionar(null);
        setBuscaProduto("");
        setResultadosBusca([]);
        onAddClose();
    };

    // Filtro + ordenação do step 2
    const itensFiltradosOrdenados = useMemo(() => {
        let list = [...itensComContagem];
        if (filtroItem.trim()) {
            const q = filtroItem.trim().toLowerCase();
            list = list.filter((i) =>
                (i.nome_produto || "").toLowerCase().includes(q) ||
                (i.codigo_global || "").toLowerCase().includes(q) ||
                (i.codigo_fornecedor || "").toLowerCase().includes(q) ||
                (i.codigo_fabricante || "").toLowerCase().includes(q) ||
                (i.codigo_barra || "").toLowerCase().includes(q) ||
                String(i.produto_id).includes(q)
            );
        }
        switch (ordItem) {
            case "nome_asc":
                list.sort((a, b) => (a.nome_produto || "").localeCompare(b.nome_produto || ""));
                break;
            case "sistema_desc":
                list.sort((a, b) => (b.quantidade_sistema || 0) - (a.quantidade_sistema || 0));
                break;
            case "diff_desc":
                list.sort((a, b) =>
                    Math.abs((b.quantidade_contada - b.quantidade_sistema)) -
                    Math.abs((a.quantidade_contada - a.quantidade_sistema))
                );
                break;
            case "ordem":
            default:
                list.sort((a, b) => (a._ordem_original ?? 0) - (b._ordem_original ?? 0));
        }
        return list;
    }, [itensComContagem, filtroItem, ordItem]);

    // Step 3 filtro por status
    const itensRevisao = useMemo(() => {
        switch (filtroStatusRev) {
            case "diferenca":
                return itensComContagem.filter((i) => i.quantidade_contada !== i.quantidade_sistema);
            case "aumentou":
                return itensComContagem.filter((i) => i.quantidade_contada > i.quantidade_sistema);
            case "diminuiu":
                return itensComContagem.filter((i) => i.quantidade_contada < i.quantidade_sistema && i.quantidade_contada > 0);
            case "removidos":
                return itensComContagem.filter((i) => i.quantidade_contada === 0 && i.quantidade_sistema > 0);
            case "conferido":
            case "divergencia":
            case "nao_encontrado":
            case "deslocado":
            case "nao_cadastrado":
                return itensComContagem.filter((i) => i.status === filtroStatusRev);
            default:
                return itensComContagem;
        }
    }, [itensComContagem, filtroStatusRev]);

    const resumoStatus = useMemo(() => {
        const r = { conferido: 0, divergencia: 0, nao_encontrado: 0, deslocado: 0, nao_cadastrado: 0 };
        itensComContagem.forEach((i) => { r[i.status] = (r[i.status] || 0) + 1; });
        return r;
    }, [itensComContagem]);

    const totalAjustes = useMemo(
        () => itensComContagem.filter((i) => i.status !== "conferido" && i.status !== "nao_iniciado").length,
        [itensComContagem]
    );

    const totalContados = useMemo(
        () => itensComContagem.filter((i) => i._touched).length,
        [itensComContagem]
    );

    // Itens existentes que o usuário ainda não tocou
    const itensNaoIniciados = useMemo(
        () => itensComContagem.filter((i) => i.status === "nao_iniciado"),
        [itensComContagem]
    );

    // Step nav
    const avancarParaRevisao = async () => {
        const algumInvalido = itens.some((i) => {
            const raw = i.quantidade_contada_str;
            return raw === "" || raw === null || raw === undefined || isNaN(parseInt(raw, 10));
        });
        if (algumInvalido) {
            toast({ title: "Há contagens em branco", description: "Use 0 para ausentes.", status: "warning", duration: 3500 });
            return;
        }
        await flushAgora();
        setStep(3);
    };

    const tentarVoltarStep = () => {
        if (step === 2) {
            const comecou = andamento || itens.some((i) => i._touched || i.tipo_origem !== "existente");
            if (comecou) { onVoltarOpen(); return; }
            sairDaContagem();
        } else if (step === 3) {
            setStep(2);
        }
    };

    /** Consome a entrada de histórico criada para segurar o botão voltar. */
    const consumirGuardaVoltar = () => {
        if (guardaVoltarRef.current <= 0) return;
        guardaVoltarRef.current -= 1;
        ignorarPopRef.current = true;
        window.history.back();
        setTimeout(() => { ignorarPopRef.current = false; }, 300);
    };

    /** Sai da contagem sem perder nada: o que foi contado fica salvo no servidor. */
    const sairDaContagem = async () => {
        onVoltarClose();
        await flushAgora();
        limparFilaSalvamento();
        consumirGuardaVoltar();
        posicaoRef.current = "";
        setStep(1);
        setPosicaoSelecionada("");
        setItens([]);
        setAndamento(null);
        setObservacao("");
    };

    const abrirDescartar = (posicao) => { setPosicaoParaDescartar(posicao); onDescOpen(); };

    const confirmarDescarte = async () => {
        const alvo = posicaoParaDescartar;
        if (!alvo) return;
        setDescartando(true);
        try {
            limparFilaSalvamento();
            await descartarRevisaoAndamento(alvo);
            toast({ title: "Contagem descartada", status: "info", duration: 2000 });
            if (step === 2 && posicaoSelecionada === alvo) await selecionarPosicao(alvo);
            else await carregarPosicoes();
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao descartar a contagem", status: "error", duration: 3000 });
        } finally {
            setDescartando(false);
            onDescClose();
            setPosicaoParaDescartar(null);
        }
    };

    const confirmarRevisao = async () => {
        onAplClose();
        setSalvando(true);
        try {
            // Grava e encerra o autosave antes de aplicar: o POST apaga a
            // conferência em andamento, e um salvamento atrasado a recriaria.
            await flushAgora();
            limparFilaSalvamento();
            const payload = {
                posicao: posicaoSelecionada,
                observacao: observacao || null,
                itens: itensComContagem.map((i) => ({
                    produto_id: i.produto_id,
                    nome_produto: i.nome_produto,
                    codigo_global: i.codigo_global,
                    codigo_fornecedor: i.codigo_fornecedor,
                    quantidade_sistema: i.quantidade_sistema,
                    quantidade_contada: i.quantidade_contada,
                    status: i.status,
                    // Itens "não iniciados" não alteram estoque
                    acao: i.status === "nao_iniciado" ? "pular" : i.acao,
                    posicoes_origem: i.posicoes_existentes || null,
                })),
            };
            const res = await salvarRevisaoEstoque(payload);
            setRevisaoSalvaId(res.data.data.id);
            posicaoRef.current = "";
            setAndamento(null);
            setStep(4);
            toast({ title: "Revisão aplicada com sucesso", status: "success", duration: 2500 });
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Falha desconhecida ao salvar";
            toast({ title: "Erro ao aplicar revisão", description: msg, status: "error", duration: 5000 });
        } finally {
            setSalvando(false);
        }
    };

    const novaRevisao = () => {
        limparFilaSalvamento();
        consumirGuardaVoltar();
        posicaoRef.current = "";
        setStep(1);
        setPosicaoSelecionada("");
        setItens([]);
        setAndamento(null);
        setObservacao("");
        setFiltroPosicao("");
        setFiltroItem("");
        setFiltroStatusRev("todos");
        setRevisaoSalvaId(null);
        carregarPosicoes();
    };

    // Histórico
    const carregarHistorico = useCallback(async (pagina = 1) => {
        setLoadingHistorico(true);
        try {
            const li = ordHistorico.lastIndexOf("_");
            const params = {
                page: pagina,
                order_by: ordHistorico.slice(0, li),
                order_dir: ordHistorico.slice(li + 1),
            };
            if (filtroDataInicio) params.data_inicio = filtroDataInicio;
            if (filtroDataFim) params.data_fim = filtroDataFim;
            if (filtroHistPosicao.trim()) params.posicao = filtroHistPosicao.trim();
            if (filtroHistUserId) params.user_id = filtroHistUserId;
            const res = await getRevisaoEstoqueHistorico(params);
            const pag = res.data.data;
            setHistorico(pag.data || []);
            setPaginaHistorico(pag.current_page || 1);
            setUltimaPaginaHistorico(pag.last_page || 1);
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar histórico", status: "error", duration: 3000 });
        } finally {
            setLoadingHistorico(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordHistorico, filtroDataInicio, filtroDataFim, filtroHistPosicao, filtroHistUserId]);

    const carregarUsuariosHistorico = async () => {
        try {
            const res = await getRevisaoEstoqueUsuarios();
            setUsuariosDisponiveis(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const limparFiltrosHistorico = () => {
        setFiltroDataInicio("");
        setFiltroDataFim("");
        setFiltroHistPosicao("");
        setFiltroHistUserId("");
        setOrdHistorico("applied_at_desc");
    };

    const abrirDetalhe = async (id) => {
        setDetalheRevisao(null);
        setLoadingDetalhe(true);
        onDetalheOpen();
        try {
            const res = await getRevisaoEstoqueDetalhe(id);
            setDetalheRevisao(res.data.data);
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar detalhes", status: "error", duration: 3000 });
        } finally {
            setLoadingDetalhe(false);
        }
    };

    const formatarData = (str) => {
        if (!str) return "—";
        try { return format(parseISO(str), "dd/MM/yyyy HH:mm", { locale: ptBR }); }
        catch { return str; }
    };

    const posicoesFiltradas = useMemo(() => {
        const q = filtroPosicao.trim().toLowerCase();
        if (!q) return posicoes;
        return posicoes.filter((p) => (p.localizacao || "").toLowerCase().includes(q));
    }, [posicoes, filtroPosicao]);

    // Loading global para operações pesadas (aplicar revisão)
    const loading = salvando;

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            {loading && <Preloader />}
            <Box maxW="1100px" mx="auto" px={{ base: 3, md: 6 }} pt={{ base: 2, md: 3 }} pb={{ base: 4, md: 6 }}>
                <Flex
                    align={{ base: "flex-start", md: "center" }}
                    justify="space-between" gap={3}
                    borderBottom="2px solid" borderColor="gray.200"
                    mb={4} direction={{ base: "column", md: "row" }}
                >
                    <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="gray.800" lineHeight={1.15}>
                            Revisão de Estoque
                        </Text>
                        <Text fontSize="11px" color="gray.500" mt="1px">Conferência física por posição</Text>
                    </Box>
                    <Flex gap={0} alignSelf={{ base: "stretch", md: "flex-end" }}>
                        {[
                            { key: "nova", label: "Nova Revisão", icon: <ClipboardList size={14} /> },
                            { key: "historico", label: "Histórico", icon: <History size={14} /> },
                        ].map((tab) => (
                            <Button key={tab.key} variant="unstyled"
                                display="flex" alignItems="center" gap={2}
                                px={4} py={2} h="auto" flexShrink={0}
                                fontSize="sm" fontWeight={activeTab === tab.key ? "semibold" : "medium"}
                                color={activeTab === tab.key ? ACCENT : "gray.500"}
                                borderBottom="2px solid"
                                borderColor={activeTab === tab.key ? ACCENT : "transparent"}
                                borderRadius={0} mb="-2px"
                                onClick={() => setActiveTab(tab.key)}
                                leftIcon={tab.icon}
                                _hover={{ color: activeTab === tab.key ? ACCENT : "gray.700" }}>
                                {tab.label}
                            </Button>
                        ))}
                    </Flex>
                </Flex>

                {/* ═══ NOVA REVISÃO ═══ */}
                {activeTab === "nova" && (
                    <Box>
                        <StepperRevisao current={step} />

                        {/* ── Step 1 ── */}
                        {step === 1 && (
                            <Box>
                                <Text fontWeight="semibold" color="gray.700" mb={3} fontSize="sm">
                                    Selecione a posição a revisar
                                </Text>

                                {resumoSaude && (
                                    <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                                        boxShadow="0 1px 2px rgba(0,0,0,0.04)" p={3} mb={4}>
                                        <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                                            <Flex align="center" gap={3} minW={0}>
                                                <Flex direction="column">
                                                    <Flex align="center" gap={0.5}>
                                                        <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">
                                                            Saúde do estoque
                                                        </Text>
                                                        <AjudaSaude />
                                                    </Flex>
                                                    <Flex align="baseline" gap={1.5}>
                                                        <Text fontSize="2xl" fontWeight="bold" lineHeight="1"
                                                            color={SAUDE_FAIXAS[resumoSaude.saude_media >= 67 ? "boa" : resumoSaude.saude_media >= 34 ? "atencao" : "critica"].cor}>
                                                            {resumoSaude.saude_media}%
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">
                                                            média de {resumoSaude.total_posicoes} posições
                                                        </Text>
                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                            <Flex gap={1.5} wrap="wrap">
                                                {["boa", "atencao", "critica", "nunca"].map((f) => (
                                                    <Badge key={f} px={2} py={1} borderRadius="md" cursor="pointer"
                                                        bg={filtroFaixa === f ? SAUDE_FAIXAS[f].cor : SAUDE_FAIXAS[f].bg}
                                                        color={filtroFaixa === f ? "white" : SAUDE_FAIXAS[f].texto}
                                                        fontWeight="semibold" fontSize="11px" textTransform="none"
                                                        onClick={() => setFiltroFaixa(filtroFaixa === f ? "" : f)}
                                                        _hover={{ opacity: 0.85 }} transition="all 0.15s">
                                                        {SAUDE_FAIXAS[f].label} {resumoSaude[f]}
                                                    </Badge>
                                                ))}
                                            </Flex>
                                        </Flex>
                                        <Flex mt={2.5} h="6px" borderRadius="full" overflow="hidden" bg="gray.100">
                                            {["boa", "atencao", "critica", "nunca"].map((f) => (
                                                resumoSaude[f] > 0 ? (
                                                    <Box key={f} bg={SAUDE_FAIXAS[f].cor}
                                                        w={`${(resumoSaude[f] / Math.max(resumoSaude.total_posicoes, 1)) * 100}%`} />
                                                ) : null
                                            ))}
                                        </Flex>
                                    </Box>
                                )}

                                <Flex gap={2} mb={4} direction={{ base: "column", sm: "row" }}>
                                    <InputGroup size="md" flex="1">
                                        <InputLeftElement><Search size={16} color="#9CA3AF" /></InputLeftElement>
                                        <Input placeholder="Filtrar posições..."
                                            value={filtroPosicao}
                                            onChange={(e) => setFiltroPosicao(e.target.value)}
                                            bg="white" borderRadius="lg" pr={filtroPosicao ? 10 : 4}
                                            _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                        {filtroPosicao && (
                                            <InputRightElement>
                                                <IconButton icon={<X size={14} />} size="xs" variant="ghost"
                                                    color="gray.400" _hover={{ color: "gray.700", bg: "gray.100" }}
                                                    onClick={() => setFiltroPosicao("")}
                                                    aria-label="Limpar" borderRadius="full" />
                                            </InputRightElement>
                                        )}
                                    </InputGroup>
                                    <Select w={{ base: "full", sm: "190px" }} size="md" bg="white" borderRadius="lg"
                                        value={filtroFaixa} onChange={(e) => setFiltroFaixa(e.target.value)}
                                        icon={<Filter size={14} />}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                        <option value="">Todas as saúdes</option>
                                        <option value="boa">Boa (67% ou mais)</option>
                                        <option value="atencao">Atenção (34% a 66%)</option>
                                        <option value="critica">Crítica (até 33%)</option>
                                        <option value="nunca">Nunca revisada</option>
                                    </Select>
                                    <Select w={{ base: "full", sm: "220px" }} size="md" bg="white" borderRadius="lg"
                                        value={ordPosicao} onChange={(e) => setOrdPosicao(e.target.value)}
                                        icon={<ArrowUpDown size={14} />}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                        <option value="localizacao_asc">Posição (A-Z)</option>
                                        <option value="localizacao_desc">Posição (Z-A)</option>
                                        <option value="saude_asc">Saúde (menor primeiro)</option>
                                        <option value="saude_desc">Saúde (maior primeiro)</option>
                                        <option value="total_itens_desc">Mais itens</option>
                                        <option value="total_itens_asc">Menos itens</option>
                                        <option value="total_quantidade_desc">Mais unidades</option>
                                        <option value="total_quantidade_asc">Menos unidades</option>
                                    </Select>
                                </Flex>

                                {loadingPosicoes ? (
                                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3}>
                                        {[...Array(8)].map((_, i) => (
                                            <Skeleton key={i} h="80px" borderRadius="xl" startColor="gray.100" endColor="gray.200" />
                                        ))}
                                    </SimpleGrid>
                                ) : posicoesFiltradas.length === 0 ? (
                                    <Flex direction="column" align="center" py={12} gap={3}>
                                        <Flex w="60px" h="60px" borderRadius="full" bg="gray.100" align="center" justify="center">
                                            <Icon as={Package} boxSize={7} color="gray.400" />
                                        </Flex>
                                        <Text color="gray.500" fontSize="sm">Nenhuma posição encontrada</Text>
                                    </Flex>
                                ) : (
                                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3}>
                                        {posicoesFiltradas.map((p) => {
                                            const faixa = faixaDe(p);
                                            const emAndamento = p.em_andamento;
                                            return (
                                                <Box key={p.localizacao} bg="white" borderRadius="xl"
                                                    boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                                                    border="1px solid" borderColor={emAndamento ? "orange.200" : "gray.200"}
                                                    borderLeft="3px solid" borderLeftColor={emAndamento ? ACCENT : faixa.cor}
                                                    p={3} cursor="pointer"
                                                    _hover={{ boxShadow: "0 8px 20px rgba(0,0,0,0.08)", transform: "translateY(-2px)", borderColor: "gray.300" }}
                                                    _active={{ transform: "translateY(0)" }}
                                                    transition="all 0.18s ease"
                                                    onClick={() => selecionarPosicao(p.localizacao)}>
                                                    <Flex align="center" justify="space-between" gap={1.5} mb={1} minW={0}>
                                                        <Flex align="center" gap={1.5} minW={0}>
                                                            <Icon as={MapPin} boxSize={3} color={faixa.cor} flexShrink={0} />
                                                            <Text fontWeight="bold" fontSize="md" color="gray.800" letterSpacing="tight" noOfLines={1}>
                                                                {p.localizacao}
                                                            </Text>
                                                        </Flex>
                                                        {emAndamento && (
                                                            <Badge bg="orange.50" color="orange.700" flexShrink={0}
                                                                fontSize="9px" px={1.5} py={0.5} borderRadius="md"
                                                                textTransform="none" fontWeight="bold">
                                                                Em andamento
                                                            </Badge>
                                                        )}
                                                        <Tooltip label={textoSaude(p)} placement="top" hasArrow>
                                                            <Badge px={1.5} py={0.5} borderRadius="md" flexShrink={0}
                                                                bg={faixa.bg} color={faixa.texto}
                                                                fontWeight="bold" fontSize="10px" textTransform="none">
                                                                {p.saude === null ? "nunca" : `${p.saude}%`}
                                                            </Badge>
                                                        </Tooltip>
                                                    </Flex>
                                                    <Flex gap={3} mt={1}>
                                                        <Box>
                                                            <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">Itens</Text>
                                                            <Text fontSize="sm" fontWeight="bold" color="gray.700">{p.total_itens}</Text>
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">Unid.</Text>
                                                            <Text fontSize="sm" fontWeight="bold" color="gray.700">{p.total_quantidade}</Text>
                                                        </Box>
                                                    </Flex>
                                                    {emAndamento ? (
                                                        <>
                                                            <Flex align="center" gap={1} mt={1.5}>
                                                                <Icon as={ClipboardList} boxSize={2.5} color={ACCENT} flexShrink={0} />
                                                                <Text fontSize="10px" color="orange.600" fontWeight="semibold" noOfLines={1}>
                                                                    {emAndamento.total_contados}/{emAndamento.total_itens} contados
                                                                    {emAndamento.user_name ? ` · ${primeiroNome(emAndamento.user_name)}` : ""}
                                                                </Text>
                                                            </Flex>
                                                            <Flex gap={1} mt={2}>
                                                                <Button size="xs" flex="1" h="26px" fontSize="10px"
                                                                    bg={ACCENT} color="white" fontWeight="semibold"
                                                                    _hover={{ bg: ACCENT_DARK }} _active={{ bg: ACCENT_DARK }}
                                                                    onClick={(e) => { e.stopPropagation(); selecionarPosicao(p.localizacao); }}>
                                                                    Continuar revisão
                                                                </Button>
                                                                <IconButton icon={<Trash2 size={12} />} size="xs" h="26px"
                                                                    variant="outline" borderColor="gray.200" color="gray.400"
                                                                    _hover={{ color: "red.500", borderColor: "red.200", bg: "red.50" }}
                                                                    aria-label={`Descartar contagem de ${p.localizacao}`}
                                                                    onClick={(e) => { e.stopPropagation(); abrirDescartar(p.localizacao); }} />
                                                            </Flex>
                                                        </>
                                                    ) : (
                                                        <Flex align="center" gap={1} mt={1.5}>
                                                            <Icon as={Clock} boxSize={2.5} color="gray.400" flexShrink={0} />
                                                            <Text fontSize="10px" color="gray.500" noOfLines={1}>
                                                                {p.ultima_revisao ? format(parseISO(p.ultima_revisao), "dd/MM/yy", { locale: ptBR }) : "sem revisão"}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </SimpleGrid>
                                )}
                            </Box>
                        )}

                        {/* ── Step 2 ── */}
                        {step === 2 && (
                            <Box>
                                <Flex align="center" mb={1} gap={2}>
                                    <IconButton icon={<ChevronLeft size={18} />} size="sm" variant="ghost"
                                        onClick={tentarVoltarStep} aria-label="Voltar" />
                                    <Box flex="1" minW={0}>
                                        <Text fontWeight="bold" fontSize="md" color="gray.800" isTruncated>
                                            Posição: <Text as="span" color={ACCENT}>{posicaoSelecionada}</Text>
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {itens.length} {itens.length === 1 ? "item" : "itens"} · {totalContados} contados
                                        </Text>
                                    </Box>
                                    <IndicadorSalvamento estado={estadoSalvamento} />
                                    {andamento && (
                                        <IconButton icon={<Trash2 size={15} />} size="sm" variant="ghost"
                                            color="gray.400" flexShrink={0}
                                            _hover={{ color: "red.500", bg: "red.50" }}
                                            aria-label="Descartar contagem e recomeçar"
                                            onClick={() => abrirDescartar(posicaoSelecionada)} />
                                    )}
                                </Flex>
                                <Text fontSize="10px" color="gray.400" mb={4} pl={1}>
                                    A contagem é salva sozinha a cada item. Dá para sair e continuar depois, até de outro aparelho.
                                </Text>

                                {loadingItens ? (
                                    <VStack spacing={2} align="stretch">
                                        {[...Array(5)].map((_, i) => (
                                            <Flex key={i} gap={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={3}>
                                                <Skeleton w="56px" h="56px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
                                                <Box flex="1"><SkeletonText noOfLines={2} spacing={2} skeletonHeight={3} startColor="gray.100" endColor="gray.200" /></Box>
                                                <Skeleton w="80px" h="32px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
                                            </Flex>
                                        ))}
                                    </VStack>
                                ) : (
                                    <>
                                        {/* Busca de produto */}
                                        <Box mb={3} position="relative">
                                            <InputGroup size="sm">
                                                <InputLeftElement><Plus size={14} color="#9CA3AF" /></InputLeftElement>
                                                <Input placeholder="Adicionar produto (nome / SKU / cód. global / forn. / fabr. / barra)..."
                                                    value={buscaProduto}
                                                    onChange={(e) => handleBuscaProduto(e.target.value)}
                                                    bg="white" borderRadius="lg" pr={buscaProduto ? 10 : 4}
                                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                                {loadingBusca ? (
                                                    <InputRightElement><Spinner size="xs" color={ACCENT} /></InputRightElement>
                                                ) : buscaProduto ? (
                                                    <InputRightElement>
                                                        <IconButton icon={<X size={12} />} size="xs" variant="ghost"
                                                            color="gray.400" _hover={{ color: "gray.700", bg: "gray.100" }}
                                                            onClick={() => { setBuscaProduto(""); setResultadosBusca([]); }}
                                                            aria-label="Limpar busca" borderRadius="full" />
                                                    </InputRightElement>
                                                ) : null}
                                            </InputGroup>
                                            {resultadosBusca.length > 0 && (
                                                <Box position="absolute" zIndex={10} w="100%" bg="white"
                                                    border="1px solid" borderColor="gray.200" borderRadius="lg"
                                                    shadow="lg" mt={1} maxH="360px" overflowY="auto">
                                                    {resultadosBusca.map((r) => {
                                                        const temOutras = (r.posicoes_existentes || []).length > 0;
                                                        const temAqui = r.quantidade_sistema > 0;
                                                        const iconCor = temAqui ? "blue" : temOutras ? "purple" : "orange";
                                                        const tag = temAqui ? `${r.quantidade_sistema} nesta pos.`
                                                                  : temOutras ? `${r.posicoes_existentes.length === 1 ? "1 pos" : r.posicoes_existentes.length + " pos"}: ${r.posicoes_existentes.map(p => p.localizacao).join(", ").slice(0, 40)}`
                                                                  : "Sem cadastro";
                                                        return (
                                                            <Box key={r.produto_id} px={3} py={2} cursor="pointer"
                                                                _hover={{ bg: "orange.50" }}
                                                                borderBottom="1px solid" borderColor="gray.100"
                                                                onClick={() => clicarProdutoBusca(r)}>
                                                                <Flex gap={2} align="center">
                                                                    <Box flex="1" minW={0}><ProdutoInfo item={r} compact /></Box>
                                                                    <Badge colorScheme={iconCor} fontSize="10px" flexShrink={0} maxW="180px" isTruncated>
                                                                        {tag}
                                                                    </Badge>
                                                                </Flex>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Filtros step 2 */}
                                        {itens.length > 0 && (
                                            <Flex gap={2} mb={3} direction={{ base: "column", sm: "row" }}>
                                                <InputGroup size="sm" flex="1">
                                                    <InputLeftElement><Filter size={13} color="#9CA3AF" /></InputLeftElement>
                                                    <Input placeholder="Filtrar itens da lista..."
                                                        value={filtroItem}
                                                        onChange={(e) => setFiltroItem(e.target.value)}
                                                        bg="white" borderRadius="lg" pr={filtroItem ? 9 : 3}
                                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                                    {filtroItem && (
                                                        <InputRightElement>
                                                            <IconButton icon={<X size={12} />} size="xs" variant="ghost"
                                                                color="gray.400" _hover={{ color: "gray.700", bg: "gray.100" }}
                                                                onClick={() => setFiltroItem("")}
                                                                aria-label="Limpar" borderRadius="full" />
                                                        </InputRightElement>
                                                    )}
                                                </InputGroup>
                                                <Select w={{ base: "full", sm: "200px" }} size="sm" bg="white" borderRadius="lg"
                                                    value={ordItem} onChange={(e) => setOrdItem(e.target.value)}
                                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                                    <option value="ordem">Ordem original</option>
                                                    <option value="nome_asc">Nome (A-Z)</option>
                                                    <option value="sistema_desc">Mais qtd. sistema</option>
                                                    <option value="diff_desc">Maior diferença</option>
                                                </Select>
                                            </Flex>
                                        )}

                                        {/* Lista com cabeçalho sticky */}
                                        {itens.length === 0 ? (
                                            <Flex direction="column" align="center" py={10} gap={2}>
                                                <Icon as={AlertCircle} boxSize={8} color="gray.300" />
                                                <Text color="gray.400" textAlign="center" px={4}>
                                                    Nenhum item nesta posição. Use o campo acima para adicionar produtos.
                                                </Text>
                                            </Flex>
                                        ) : itensFiltradosOrdenados.length === 0 ? (
                                            <Text color="gray.400" textAlign="center" py={6}>Nenhum item bate com o filtro.</Text>
                                        ) : (
                                            <>
                                                {/* Header de colunas */}
                                                <Flex
                                                    display={{ base: "none", md: "flex" }}
                                                    align="center" gap={3}
                                                    px={3} py={2} mb={2}
                                                    bg="gray.100" borderRadius="md"
                                                    fontSize="10px" fontWeight="bold"
                                                    color="gray.600" textTransform="uppercase" letterSpacing="wide"
                                                    whiteSpace="nowrap"
                                                >
                                                    <Box flex="1">Produto</Box>
                                                    <Box w="120px" textAlign="center">Status</Box>
                                                    <Box w="64px" textAlign="center">Sistema</Box>
                                                    <Box w="120px" textAlign="center">Contado</Box>
                                                    <Box w="72px" textAlign="center">Diferença</Box>
                                                    <Box w="24px" />
                                                </Flex>

                                                <VStack spacing={2} align="stretch" mb={4}>
                                                    {itensFiltradosOrdenados.map((item) => {
                                                        const diff = item.quantidade_contada - item.quantidade_sistema;
                                                        const corBordaEsq =
                                                            item.tipo_origem === "deslocado" ? "purple.400"
                                                            : item.tipo_origem === "nao_cadastrado" ? "orange.400"
                                                            : item.status === "nao_iniciado" ? "gray.200"
                                                            : diff > 0 ? "green.400"
                                                            : diff < 0 ? "red.400"
                                                            : "gray.200";
                                                        const corBordaInput = item.status === "nao_iniciado" ? "gray.300"
                                                            : diff !== 0 ? (diff > 0 ? "green.400" : "red.400")
                                                            : "gray.300";
                                                        return (
                                                            <Box key={item.produto_id} bg="white" borderRadius="xl" p={3}
                                                                boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                                                                border="1px solid" borderColor="gray.200"
                                                                borderLeft="3px solid" borderLeftColor={corBordaEsq}
                                                                _hover={{ boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}
                                                                transition="box-shadow 0.15s">
                                                                <Flex direction={{ base: "column", md: "row" }} gap={3} align={{ md: "center" }}>
                                                                    <Box flex="1" minW={0}>
                                                                        <ProdutoInfo item={item} onAmpliar={() => abrirImagemAmpliada(item)} />
                                                                        {item.foto_real && (
                                                                            <Box mt={2} pl="68px">
                                                                                <SeloImagemCorrigida em={item.foto_real_em} />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                    <Flex gap={3} align="center"
                                                                        justify={{ base: "space-between", md: "flex-end" }}
                                                                        flexShrink={0} wrap={{ base: "wrap", md: "nowrap" }}>
                                                                        <Box w="120px" display={{ base: "none", md: "block" }} textAlign="center">
                                                                            <StatusBadge status={item.status} />
                                                                        </Box>
                                                                        <Box textAlign="center" w="64px">
                                                                            <Text fontSize="9px" display={{ base: "block", md: "none" }} color="gray.400" textTransform="uppercase" letterSpacing="wide">Sistema</Text>
                                                                            <Text fontWeight="bold" fontSize="md" color="gray.700">{item.quantidade_sistema}</Text>
                                                                        </Box>
                                                                        <Box w="120px">
                                                                            <Text fontSize="9px" display={{ base: "block", md: "none" }} color="gray.400" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={0.5}>Contado</Text>
                                                                            <Flex justify="center">
                                                                                <InputQtd
                                                                                    value={item.quantidade_contada_str}
                                                                                    onChange={(v) => atualizarContagem(item.produto_id, v)}
                                                                                    borderColor={corBordaInput}
                                                                                    ariaLabel={`Quantidade contada de ${nomeDe(item)}`}
                                                                                />
                                                                            </Flex>
                                                                        </Box>
                                                                        <Box w="72px" textAlign="center">
                                                                            {item.status === "nao_iniciado" ? (
                                                                                <Text fontSize="11px" color="gray.400" whiteSpace="nowrap">—</Text>
                                                                            ) : diff !== 0 ? (
                                                                                <Badge colorScheme={diff > 0 ? "green" : "red"} fontSize="xs" px={2} py={1} borderRadius="md" whiteSpace="nowrap">
                                                                                    {diff > 0 ? `+${diff}` : diff}
                                                                                </Badge>
                                                                            ) : <Text fontSize="xs" color="gray.300">—</Text>}
                                                                        </Box>
                                                                        <Box display={{ base: "none", md: "block" }}>
                                                                            <IconButton icon={<X size={14} />} size="xs" variant="ghost"
                                                                                color="gray.400" _hover={{ color: "red.500", bg: "red.50" }}
                                                                                onClick={() => abrirConfirmRemover(item)}
                                                                                aria-label={`Remover ${nomeDe(item)}`} />
                                                                        </Box>
                                                                        <Box display={{ base: "block", md: "none" }}>
                                                                            <StatusBadge status={item.status} size="xs" />
                                                                            <IconButton icon={<X size={14} />} size="xs" variant="ghost" ml={1}
                                                                                color="gray.400" _hover={{ color: "red.500", bg: "red.50" }}
                                                                                onClick={() => abrirConfirmRemover(item)}
                                                                                aria-label={`Remover ${nomeDe(item)}`} />
                                                                        </Box>
                                                                    </Flex>
                                                                </Flex>
                                                            </Box>
                                                        );
                                                    })}
                                                </VStack>
                                            </>
                                        )}

                                        <Button colorScheme="orange" w="100%" size="md"
                                            onClick={avancarParaRevisao} isDisabled={itens.length === 0}
                                            bg={ACCENT}
                                            _hover={{ bg: ACCENT_DARK, transform: "translateY(-1px)", boxShadow: "md" }}
                                            _active={{ bg: ACCENT_DARK, transform: "translateY(0)" }}
                                            boxShadow="0 2px 6px rgba(246,134,51,0.25)"
                                            transition="all 0.15s" fontWeight="semibold">
                                            Revisar e confirmar
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}

                        {/* ── Step 3 ── */}
                        {step === 3 && (
                            <Box>
                                <Flex align="center" mb={4} gap={2}>
                                    <IconButton icon={<ChevronLeft size={18} />} size="sm" variant="ghost"
                                        onClick={tentarVoltarStep} aria-label="Voltar" />
                                    <Box flex="1" minW={0}>
                                        <Text fontWeight="bold" fontSize="md" color="gray.800" isTruncated>
                                            Revisão — <Text as="span" color={ACCENT}>{posicaoSelecionada}</Text>
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {itensComContagem.length} itens · {totalAjustes} {totalAjustes === 1 ? "requerendo ação" : "requerendo ações"}
                                        </Text>
                                    </Box>
                                    <IndicadorSalvamento estado={estadoSalvamento} />
                                </Flex>

                                {/* Aviso de itens não iniciados */}
                                {itensNaoIniciados.length > 0 && (
                                    <Box
                                        bg="yellow.50" border="1px solid" borderColor="yellow.300"
                                        borderLeft="4px solid" borderLeftColor="yellow.500"
                                        borderRadius="lg" p={3} mb={4}
                                    >
                                        <Flex align="center" gap={3} wrap="wrap">
                                            <Icon as={Clock} boxSize={5} color="yellow.600" flexShrink={0} />
                                            <Box flex="1" minW="200px">
                                                <Text fontSize="sm" fontWeight="bold" color="yellow.800">
                                                    {itensNaoIniciados.length} {itensNaoIniciados.length === 1 ? "item não foi iniciado" : "itens não foram iniciados"}
                                                </Text>
                                                <Text fontSize="xs" color="yellow.700" mt={0.5}>
                                                    Itens ignorados não alteram o estoque. Só são alterados os que você tocou.
                                                </Text>
                                            </Box>
                                            <Flex gap={2} flexShrink={0}>
                                                <Button size="xs" colorScheme="yellow" variant="outline"
                                                    onClick={() => setFiltroStatusRev("nao_iniciado")}>
                                                    Ver
                                                </Button>
                                                <Button size="xs" variant="ghost" color="yellow.800"
                                                    onClick={() => setStep(2)}>
                                                    Voltar
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    </Box>
                                )}

                                {/* Resumo por status */}
                                <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} gap={2} mb={4}>
                                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                                        const count = resumoStatus[key] || 0;
                                        const ativo = filtroStatusRev === key;
                                        return (
                                            <Box key={key} bg="white" borderRadius="lg" p={2}
                                                border="1px solid" borderColor={ativo ? `${cfg.cor}.400` : "gray.200"}
                                                boxShadow={ativo ? "0 2px 6px rgba(0,0,0,0.06)" : "0 1px 2px rgba(0,0,0,0.04)"}
                                                cursor="pointer"
                                                _hover={{ borderColor: `${cfg.cor}.300` }}
                                                onClick={() => setFiltroStatusRev(ativo ? "todos" : key)}
                                                transition="all 0.15s">
                                                <Flex align="center" gap={1.5} mb={0.5}>
                                                    <Icon as={cfg.icon} boxSize={3} color={`${cfg.cor}.500`} />
                                                    <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">{cfg.label}</Text>
                                                </Flex>
                                                <Text fontSize="xl" fontWeight="bold" color={count > 0 ? `${cfg.cor}.600` : "gray.400"}>
                                                    {count}
                                                </Text>
                                            </Box>
                                        );
                                    })}
                                </SimpleGrid>

                                {/* Filtros */}
                                <Flex gap={2} mb={3} align="center" wrap="wrap">
                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">Exibir:</Text>
                                    <Select size="sm" w={{ base: "full", sm: "280px" }} bg="white" borderRadius="lg"
                                        value={filtroStatusRev} onChange={(e) => setFiltroStatusRev(e.target.value)}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                        <option value="todos">Todos os itens ({itensComContagem.length})</option>
                                        <option value="diferenca">Apenas com diferença</option>
                                        <option value="aumentou">Apenas que aumentaram</option>
                                        <option value="diminuiu">Apenas que diminuíram</option>
                                        <option value="removidos">Apenas removidos (0)</option>
                                        <option value="nao_iniciado">Status: ⏱ Não iniciado ({resumoStatus.nao_iniciado || 0})</option>
                                        <option value="conferido">Status: ✅ Conferido ({resumoStatus.conferido || 0})</option>
                                        <option value="divergencia">Status: ⚠️ Divergência ({resumoStatus.divergencia || 0})</option>
                                        <option value="nao_encontrado">Status: ❌ Não encontrado ({resumoStatus.nao_encontrado || 0})</option>
                                        <option value="deslocado">Status: 📦 Deslocado ({resumoStatus.deslocado || 0})</option>
                                        <option value="nao_cadastrado">Status: ❓ Não cadastrado ({resumoStatus.nao_cadastrado || 0})</option>
                                    </Select>
                                </Flex>

                                <Box bg="white" borderRadius="xl" overflow="hidden" mb={4}
                                    boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                                    border="1px solid" borderColor="gray.200">
                                    <TableContainer>
                                        <Table size="sm">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th whiteSpace="nowrap">Produto</Th>
                                                    <Th whiteSpace="nowrap">Status</Th>
                                                    <Th whiteSpace="nowrap" isNumeric>Sistema</Th>
                                                    <Th whiteSpace="nowrap" isNumeric>Contado</Th>
                                                    <Th whiteSpace="nowrap" isNumeric>Diferença</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {itensRevisao.length === 0 ? (
                                                    <Tr><Td colSpan={5} textAlign="center" py={6} color="gray.400">Nenhum item neste filtro.</Td></Tr>
                                                ) : itensRevisao.map((item) => {
                                                    const diff = item.quantidade_contada - item.quantidade_sistema;
                                                    const ehNaoIniciado = item.status === "nao_iniciado";
                                                    return (
                                                        <Tr key={item.produto_id} bg={ehNaoIniciado ? "white" : diff !== 0 ? (diff > 0 ? "green.50" : "red.50") : "white"}>
                                                            <Td minW="240px"><ProdutoInfo item={item} compact /></Td>
                                                            <Td><StatusBadge status={item.status} /></Td>
                                                            <Td isNumeric>{item.quantidade_sistema}</Td>
                                                            <Td isNumeric fontWeight="semibold" color={ehNaoIniciado ? "gray.400" : "inherit"}>
                                                                {ehNaoIniciado ? "—" : item.quantidade_contada}
                                                            </Td>
                                                            <Td isNumeric whiteSpace="nowrap">
                                                                {ehNaoIniciado ? (
                                                                    <Text color="gray.400" fontSize="sm">—</Text>
                                                                ) : diff !== 0 ? (
                                                                    <Badge colorScheme={diff > 0 ? "green" : "red"}>
                                                                        {diff > 0 ? `+${diff}` : diff}
                                                                    </Badge>
                                                                ) : <Text color="gray.400" fontSize="sm">—</Text>}
                                                            </Td>
                                                        </Tr>
                                                    );
                                                })}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </Box>

                                <Box mb={4}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>Observação (opcional)</Text>
                                    <Textarea value={observacao}
                                        onChange={(e) => atualizarObservacao(e.target.value)}
                                        placeholder="Alguma observação sobre esta revisão..."
                                        rows={3} borderRadius="lg" bg="white" maxLength={1000}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                </Box>

                                {totalAjustes > 0 && (
                                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg" p={3} mb={4}>
                                        <Text fontSize="sm" color="orange.700" fontWeight="medium">
                                            {totalAjustes} {totalAjustes === 1 ? "ação será aplicada" : "ações serão aplicadas"} ao estoque.
                                        </Text>
                                    </Box>
                                )}

                                <Button colorScheme="orange" w="100%" size="md"
                                    isLoading={salvando} loadingText="Aplicando..."
                                    onClick={onAplOpen}
                                    bg={ACCENT}
                                    _hover={{ bg: ACCENT_DARK, transform: "translateY(-1px)", boxShadow: "md" }}
                                    _active={{ bg: ACCENT_DARK, transform: "translateY(0)" }}
                                    boxShadow="0 2px 6px rgba(246,134,51,0.25)"
                                    transition="all 0.15s" fontWeight="semibold">
                                    Confirmar e Aplicar Revisão
                                </Button>
                            </Box>
                        )}

                        {/* ── Step 4 ── */}
                        {step === 4 && (
                            <Flex direction="column" align="center" py={10} gap={4} px={4}>
                                <CheckCircle size={56} color="#48BB78" />
                                <Text fontSize="xl" fontWeight="bold" color="gray.800">Revisão aplicada!</Text>
                                <Text color="gray.500" textAlign="center" maxW="340px">
                                    Os ajustes foram registrados na posição{" "}
                                    <Text as="span" fontWeight="semibold" color={ACCENT}>{posicaoSelecionada}</Text>.
                                </Text>
                                <HStack gap={3} mt={2} wrap="wrap" justify="center">
                                    <Button variant="outline" colorScheme="orange" onClick={novaRevisao}>Nova revisão</Button>
                                    <Button colorScheme="orange" onClick={() => setActiveTab("historico")}>Ver histórico</Button>
                                </HStack>
                            </Flex>
                        )}
                    </Box>
                )}

                {/* ═══ HISTÓRICO ═══ */}
                {activeTab === "historico" && (
                    <Box>
                        <Box bg="white" borderRadius="xl" mb={4} boxShadow="0 1px 2px rgba(0,0,0,0.04)" border="1px solid" borderColor="gray.200">
                            <Flex align="center" justify="space-between" p={3} cursor="pointer"
                                onClick={() => setFiltrosAbertos(!filtrosAbertos)}
                                _hover={{ bg: "gray.50" }} borderRadius="xl" transition="background 0.15s">
                                <Flex align="center" gap={2}>
                                    <Icon as={Filter} boxSize={4} color="gray.500" />
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">Filtros</Text>
                                    {(filtroDataInicio || filtroDataFim || filtroHistPosicao || filtroHistUserId) && (
                                        <Badge colorScheme="orange" fontSize="10px">Ativos</Badge>
                                    )}
                                </Flex>
                                <Icon as={filtrosAbertos ? ChevronUp : ChevronDown} boxSize={4} color="gray.400" />
                            </Flex>
                            <Collapse in={filtrosAbertos} animateOpacity>
                                <Box px={3} pb={3} pt={1} borderTop="1px solid" borderColor="gray.100">
                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} gap={2} pt={3}>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Data inicial</Text>
                                            <Input type="date" size="sm" value={filtroDataInicio} bg="white"
                                                onChange={(e) => setFiltroDataInicio(e.target.value)}
                                                _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Data final</Text>
                                            <Input type="date" size="sm" value={filtroDataFim} bg="white"
                                                onChange={(e) => setFiltroDataFim(e.target.value)}
                                                _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Posição</Text>
                                            <InputGroup size="sm">
                                                <Input placeholder="Ex: A-3-3" value={filtroHistPosicao} bg="white"
                                                    onChange={(e) => setFiltroHistPosicao(e.target.value)}
                                                    pr={filtroHistPosicao ? 9 : 3}
                                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                                {filtroHistPosicao && (
                                                    <InputRightElement>
                                                        <IconButton icon={<X size={12} />} size="xs" variant="ghost"
                                                            color="gray.400" _hover={{ color: "gray.700", bg: "gray.100" }}
                                                            onClick={() => setFiltroHistPosicao("")}
                                                            aria-label="Limpar" borderRadius="full" />
                                                    </InputRightElement>
                                                )}
                                            </InputGroup>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Responsável</Text>
                                            <Select size="sm" value={filtroHistUserId} bg="white"
                                                onChange={(e) => setFiltroHistUserId(e.target.value)}
                                                _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                                <option value="">Todos</option>
                                                {usuariosDisponiveis.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                            </Select>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Ordenar por</Text>
                                            <Select size="sm" value={ordHistorico} bg="white"
                                                onChange={(e) => setOrdHistorico(e.target.value)}
                                                _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                                <option value="applied_at_desc">Data (mais recente)</option>
                                                <option value="applied_at_asc">Data (mais antigo)</option>
                                                <option value="posicao_asc">Posição (A-Z)</option>
                                                <option value="posicao_desc">Posição (Z-A)</option>
                                                <option value="total_ajustes_desc">Mais ajustes</option>
                                                <option value="total_itens_desc">Mais itens</option>
                                            </Select>
                                        </Box>
                                    </SimpleGrid>
                                    <Flex gap={2} mt={3} justify="flex-end" wrap="wrap">
                                        <Button size="sm" variant="ghost" onClick={limparFiltrosHistorico}>Limpar</Button>
                                        <Button size="sm" colorScheme="orange" onClick={() => carregarHistorico(1)}>Aplicar filtros</Button>
                                    </Flex>
                                </Box>
                            </Collapse>
                        </Box>

                        {loadingHistorico ? (
                            <VStack spacing={2} align="stretch">
                                {[...Array(6)].map((_, i) => <Skeleton key={i} h="44px" borderRadius="md" startColor="gray.100" endColor="gray.200" />)}
                            </VStack>
                        ) : historico.length === 0 ? (
                            <Flex direction="column" align="center" py={12} gap={3}>
                                <Flex w="60px" h="60px" borderRadius="full" bg="gray.100" align="center" justify="center">
                                    <Icon as={History} boxSize={7} color="gray.400" />
                                </Flex>
                                <Text color="gray.500" fontSize="sm">Nenhuma revisão encontrada.</Text>
                            </Flex>
                        ) : (
                            <>
                                {/* Desktop: tabela */}
                                <Box display={{ base: "none", md: "block" }}
                                    bg="white" borderRadius="xl" overflow="hidden" mb={4}
                                    boxShadow="0 1px 2px rgba(0,0,0,0.04)" border="1px solid" borderColor="gray.200">
                                    <TableContainer>
                                        <Table size="sm">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th>Data</Th><Th>Posição</Th><Th>Responsável</Th>
                                                    <Th isNumeric>Itens</Th><Th isNumeric>Ajustes</Th><Th />
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {historico.map((r) => (
                                                    <Tr key={r.id} _hover={{ bg: "gray.50" }}>
                                                        <Td fontSize="xs" whiteSpace="nowrap">{formatarData(r.applied_at)}</Td>
                                                        <Td><Badge colorScheme="orange" fontSize="xs">{r.posicao}</Badge></Td>
                                                        <Td fontSize="sm">{r.user_name}</Td>
                                                        <Td isNumeric>{r.total_itens}</Td>
                                                        <Td isNumeric>{r.total_ajustes > 0 ? <Badge colorScheme="red">{r.total_ajustes}</Badge> : <Text color="gray.400" fontSize="xs">—</Text>}</Td>
                                                        <Td><Button size="xs" variant="outline" colorScheme="orange" onClick={() => abrirDetalhe(r.id)}>Ver</Button></Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </Box>

                                {/* Mobile: cards */}
                                <VStack display={{ base: "flex", md: "none" }} spacing={3} align="stretch" mb={4}>
                                    {historico.map((r) => (
                                        <Box key={r.id} bg="white" borderRadius="xl"
                                            border="1px solid" borderColor="gray.200"
                                            p={3} boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                                            _hover={{ boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}
                                            transition="box-shadow 0.15s">
                                            <Flex justify="space-between" align="flex-start" mb={2} gap={2}>
                                                <Badge colorScheme="orange" px={2} py={1} fontSize="xs" flexShrink={0}>
                                                    {r.posicao}
                                                </Badge>
                                                <Text fontSize="10px" color="gray.500" textAlign="right" whiteSpace="nowrap">
                                                    {formatarData(r.applied_at)}
                                                </Text>
                                            </Flex>

                                            <Box mb={2}>
                                                <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">Responsável</Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.700">{r.user_name}</Text>
                                            </Box>

                                            {r.observacao && (
                                                <Box mb={2} bg="gray.50" p={2} borderRadius="md">
                                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">Observação</Text>
                                                    <Text fontSize="xs" color="gray.700" noOfLines={2}>{r.observacao}</Text>
                                                </Box>
                                            )}

                                            <Flex gap={4} mb={3}>
                                                <Box>
                                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">Itens</Text>
                                                    <Text fontSize="lg" fontWeight="bold" color="gray.700">{r.total_itens}</Text>
                                                </Box>
                                                <Box>
                                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">Ajustes</Text>
                                                    <Text fontSize="lg" fontWeight="bold" color={r.total_ajustes > 0 ? "red.500" : "gray.400"}>
                                                        {r.total_ajustes}
                                                    </Text>
                                                </Box>
                                            </Flex>

                                            <Button size="sm" w="full" variant="outline" colorScheme="orange"
                                                onClick={() => abrirDetalhe(r.id)}>
                                                Ver detalhes
                                            </Button>
                                        </Box>
                                    ))}
                                </VStack>
                                {ultimaPaginaHistorico > 1 && (
                                    <HStack justify="center" gap={2} wrap="wrap">
                                        <Button size="sm" variant="outline" isDisabled={paginaHistorico === 1}
                                            onClick={() => carregarHistorico(paginaHistorico - 1)}>Anterior</Button>
                                        <Text fontSize="sm" color="gray.500">{paginaHistorico} / {ultimaPaginaHistorico}</Text>
                                        <Button size="sm" variant="outline" isDisabled={paginaHistorico === ultimaPaginaHistorico}
                                            onClick={() => carregarHistorico(paginaHistorico + 1)}>Próxima</Button>
                                    </HStack>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {/* ═══ Modal: Adicionar produto externo (escolher ação) ═══ */}
            <ImagemAmpliada
                item={itemAmpliado}
                isOpen={imagemAmpliada.aberta}
                onFechar={fecharImagemAmpliada}
                onCorrigir={corrigirImagemAmpliada}
            />

            <CorrigirImagemModal
                isOpen={!!itemCorrigirImagem}
                onClose={fecharCorrigirImagem}
                product={itemCorrigirImagem}
                userId={usuarioId}
                onConfirmed={imagemCorrigida}
            />

            <Modal isOpen={isAddOpen} onClose={onAddClose} size={{ base: "full", md: "lg" }} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        {produtoParaAdicionar?._kind === "deslocado"
                            ? "📦 Produto em outra posição"
                            : "❓ Produto sem cadastro"}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {produtoParaAdicionar && (
                            <VStack align="stretch" spacing={4}>
                                <ProdutoInfo item={produtoParaAdicionar} />

                                {produtoParaAdicionar._kind === "deslocado" ? (
                                    <>
                                        <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="md" p={3}>
                                            <Text fontSize="sm" color="purple.800" mb={2} fontWeight="semibold">
                                                No sistema este produto está em:
                                            </Text>
                                            <Flex wrap="wrap" gap={2}>
                                                {(produtoParaAdicionar.posicoes_existentes || []).map((pe, idx) => (
                                                    <Badge key={idx} colorScheme="purple" px={2} py={1} fontSize="11px">
                                                        {pe.localizacao} · {pe.quantidade} un.
                                                    </Badge>
                                                ))}
                                            </Flex>
                                        </Box>

                                        <Text fontSize="sm" color="gray.600">
                                            Você encontrou este produto fisicamente em <Text as="span" fontWeight="semibold" color={ACCENT}>{posicaoSelecionada}</Text>. O que deseja fazer?
                                        </Text>

                                        <VStack align="stretch" spacing={2}>
                                            <Button colorScheme="purple" variant="outline"
                                                justifyContent="flex-start" h="auto" py={3}
                                                onClick={() => confirmarAdicao("transferir_fisico")}>
                                                <VStack align="flex-start" spacing={0} w="full">
                                                    <Text fontWeight="semibold" fontSize="sm">Transferir fisicamente</Text>
                                                    <Text fontSize="11px" color="gray.600" whiteSpace="normal" textAlign="left">
                                                        Vou mover o item físico para a posição registrada. <b>Sistema não será alterado.</b>
                                                    </Text>
                                                </VStack>
                                            </Button>
                                            <Button colorScheme="orange" variant="outline"
                                                justifyContent="flex-start" h="auto" py={3}
                                                onClick={() => confirmarAdicao("atualizar_sistema")}>
                                                <VStack align="flex-start" spacing={0} w="full">
                                                    <Text fontWeight="semibold" fontSize="sm">Atualizar sistema</Text>
                                                    <Text fontSize="11px" color="gray.600" whiteSpace="normal" textAlign="left">
                                                        Remover das outras posições e registrar em <b>{posicaoSelecionada}</b>.
                                                    </Text>
                                                </VStack>
                                            </Button>
                                        </VStack>
                                    </>
                                ) : (
                                    <>
                                        <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                                            <Text fontSize="sm" color="orange.800" fontWeight="semibold">
                                                Este produto não possui localização registrada no sistema.
                                            </Text>
                                        </Box>

                                        <Text fontSize="sm" color="gray.600">
                                            Você encontrou este produto fisicamente em <Text as="span" fontWeight="semibold" color={ACCENT}>{posicaoSelecionada}</Text>. O que deseja fazer?
                                        </Text>

                                        <VStack align="stretch" spacing={2}>
                                            <Button colorScheme="green" variant="outline"
                                                justifyContent="flex-start" h="auto" py={3}
                                                onClick={() => confirmarAdicao("cadastrar")}>
                                                <VStack align="flex-start" spacing={0} w="full">
                                                    <Text fontWeight="semibold" fontSize="sm">Cadastrar nesta posição</Text>
                                                    <Text fontSize="11px" color="gray.600" whiteSpace="normal" textAlign="left">
                                                        Criar registro do produto em <b>{posicaoSelecionada}</b> com a quantidade contada.
                                                    </Text>
                                                </VStack>
                                            </Button>
                                            <Button colorScheme="gray" variant="outline"
                                                justifyContent="flex-start" h="auto" py={3}
                                                onClick={() => confirmarAdicao("revisao_manual")}>
                                                <VStack align="flex-start" spacing={0} w="full">
                                                    <Text fontWeight="semibold" fontSize="sm">Sinalizar revisão manual</Text>
                                                    <Text fontSize="11px" color="gray.600" whiteSpace="normal" textAlign="left">
                                                        Apenas registrar para análise posterior. <b>Sistema não será alterado.</b>
                                                    </Text>
                                                </VStack>
                                            </Button>
                                        </VStack>
                                    </>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* ═══ Modal: Detalhe revisão ═══ */}
            <Modal isOpen={isDetalheOpen} onClose={onDetalheClose} size={{ base: "full", md: "4xl" }} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        {detalheRevisao ? `Revisão #${detalheRevisao.id} — ${detalheRevisao.posicao}` : "Detalhes"}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {loadingDetalhe ? (
                            <Flex justify="center" py={8}><Spinner color={ACCENT} /></Flex>
                        ) : detalheRevisao ? (
                            <VStack align="stretch" spacing={3}>
                                <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
                                    <Box>
                                        <Text fontSize="xs" color="gray.400">Data</Text>
                                        <Text fontSize="sm" fontWeight="medium">{formatarData(detalheRevisao.applied_at)}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.400">Responsável</Text>
                                        <Text fontSize="sm" fontWeight="medium">{detalheRevisao.user_name}</Text>
                                    </Box>
                                    {detalheRevisao.observacao && (
                                        <Box gridColumn={{ sm: "span 2" }}>
                                            <Text fontSize="xs" color="gray.400">Observação</Text>
                                            <Text fontSize="sm">{detalheRevisao.observacao}</Text>
                                        </Box>
                                    )}
                                </SimpleGrid>
                                <Divider />

                                {/* Desktop: tabela */}
                                <Box display={{ base: "none", md: "block" }} borderRadius="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
                                    <Table size="sm" sx={{ tableLayout: "fixed", width: "100%" }}>
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th w="auto" whiteSpace="nowrap">Produto</Th>
                                                <Th w="150px" whiteSpace="nowrap">Status</Th>
                                                <Th w="80px" whiteSpace="nowrap" isNumeric>Sistema</Th>
                                                <Th w="80px" whiteSpace="nowrap" isNumeric>Contado</Th>
                                                <Th w="80px" whiteSpace="nowrap" isNumeric>Dif.</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {(detalheRevisao.itens || []).map((item) => (
                                                <Tr key={item.id} bg={item.diferenca != 0 ? (item.diferenca > 0 ? "green.50" : "red.50") : "white"}>
                                                    <Td whiteSpace="normal">
                                                        <ProdutoInfo item={item} compact />
                                                    </Td>
                                                    <Td>{item.status ? <StatusBadge status={item.status} /> : <Text fontSize="xs" color="gray.400">—</Text>}</Td>
                                                    <Td isNumeric>{item.quantidade_sistema}</Td>
                                                    <Td isNumeric fontWeight="semibold">{item.quantidade_contada}</Td>
                                                    <Td isNumeric>
                                                        {item.diferenca != 0 ? (
                                                            <Badge colorScheme={item.diferenca > 0 ? "green" : "red"}>
                                                                {item.diferenca > 0 ? `+${item.diferenca}` : item.diferenca}
                                                            </Badge>
                                                        ) : <Text color="gray.400" fontSize="sm">—</Text>}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>

                                {/* Mobile: cards */}
                                <VStack display={{ base: "flex", md: "none" }} spacing={2} align="stretch">
                                    {(detalheRevisao.itens || []).map((item) => {
                                        const corBorda = item.diferenca > 0 ? "green.400" : item.diferenca < 0 ? "red.400" : "gray.200";
                                        return (
                                            <Box key={item.id} bg="white" borderRadius="lg" p={3}
                                                border="1px solid" borderColor="gray.200"
                                                borderLeft="3px solid" borderLeftColor={corBorda}>
                                                <Flex justify="flex-end" mb={2}>
                                                    {item.status && <StatusBadge status={item.status} size="xs" />}
                                                </Flex>
                                                <Box mb={3}><ProdutoInfo item={item} compact /></Box>
                                                <Flex gap={4} align="center">
                                                    <Box>
                                                        <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">Sistema</Text>
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.700">{item.quantidade_sistema}</Text>
                                                    </Box>
                                                    <Box>
                                                        <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">Contado</Text>
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.700">{item.quantidade_contada}</Text>
                                                    </Box>
                                                    <Box ml="auto">
                                                        {item.diferenca != 0 ? (
                                                            <Badge colorScheme={item.diferenca > 0 ? "green" : "red"} fontSize="xs" px={2} py={1}>
                                                                {item.diferenca > 0 ? `+${item.diferenca}` : item.diferenca}
                                                            </Badge>
                                                        ) : <Text color="gray.400" fontSize="xs">sem dif.</Text>}
                                                    </Box>
                                                </Flex>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </VStack>
                        ) : null}
                    </ModalBody>
                    <ModalFooter><Button onClick={onDetalheClose} size="sm">Fechar</Button></ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirm: Remover */}
            <AlertDialog isOpen={isRemOpen} leastDestructiveRef={confirmRemoverRef} onClose={onRemClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Remover item da revisão?</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Tem certeza que deseja remover <Text as="span" fontWeight="semibold">{nomeDe(itemARemover)}</Text> da lista?
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={confirmRemoverRef} onClick={onRemClose} size="sm">Cancelar</Button>
                            <Button colorScheme="red" onClick={removerItemConfirmado} ml={3} size="sm">Remover</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Confirm: Aplicar */}
            <AlertDialog isOpen={isAplOpen} leastDestructiveRef={confirmAplicarRef} onClose={onAplClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Confirmar aplicação da revisão?</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            {itensNaoIniciados.length > 0 && (
                                <Box bg="yellow.50" border="1px solid" borderColor="yellow.300"
                                    borderLeft="4px solid" borderLeftColor="yellow.500"
                                    borderRadius="md" p={3} mb={3}>
                                    <Flex align="flex-start" gap={2}>
                                        <Icon as={Clock} boxSize={4} color="yellow.600" mt={0.5} flexShrink={0} />
                                        <Box>
                                            <Text fontSize="sm" fontWeight="bold" color="yellow.800">
                                                {itensNaoIniciados.length} {itensNaoIniciados.length === 1 ? "item não iniciado" : "itens não iniciados"}
                                            </Text>
                                            <Text fontSize="xs" color="yellow.700" mt={1}>
                                                Estes itens serão <b>ignorados</b> — o estoque deles não será alterado.
                                            </Text>
                                        </Box>
                                    </Flex>
                                </Box>
                            )}

                            Você está prestes a aplicar <Text as="span" fontWeight="bold" color="orange.500">{totalAjustes}</Text> {totalAjustes === 1 ? "ação" : "ações"} no estoque da posição <Text as="span" fontWeight="semibold">{posicaoSelecionada}</Text>.
                            <Box mt={3} bg="gray.50" p={2} borderRadius="md">
                                <SimpleGrid columns={2} gap={1}>
                                    {Object.entries(resumoStatus).filter(([, c]) => c > 0).map(([k, c]) => {
                                        const cfg = STATUS_CONFIG[k];
                                        return (
                                            <Text key={k} fontSize="xs" color="gray.700">
                                                {cfg.emoji} {cfg.label}: <b>{c}</b>
                                            </Text>
                                        );
                                    })}
                                </SimpleGrid>
                            </Box>
                            <Text mt={3} fontSize="xs" color="gray.500">Esta ação não pode ser desfeita automaticamente.</Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={confirmAplicarRef} onClick={onAplClose} size="sm">Cancelar</Button>
                            <Button
                                colorScheme="orange"
                                onClick={confirmarRevisao} ml={3} size="sm" isLoading={salvando}
                            >
                                Confirmar e aplicar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Confirm: Sair da contagem (nada é perdido) */}
            <AlertDialog isOpen={isVoltarOpen} leastDestructiveRef={confirmVoltarRef} onClose={onVoltarClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Sair da contagem?</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            <Text>
                                A contagem de <Text as="span" fontWeight="semibold">{posicaoSelecionada}</Text> fica
                                salva. Você pode continuar depois, por aqui ou por outro aparelho.
                            </Text>
                            <Flex align="center" gap={1.5} mt={3} bg="green.50" borderRadius="md" px={2.5} py={2}>
                                <Icon as={Check} boxSize={3.5} color="green.600" flexShrink={0} />
                                <Text fontSize="xs" color="green.800">
                                    {totalContados} {totalContados === 1 ? "item contado" : "itens contados"} até agora.
                                </Text>
                            </Flex>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={confirmVoltarRef} onClick={onVoltarClose} size="sm">Continuar contando</Button>
                            <Button colorScheme="orange" onClick={sairDaContagem} ml={3} size="sm">Sair</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Confirm: Descartar a contagem em andamento */}
            <AlertDialog isOpen={isDescOpen} leastDestructiveRef={confirmDescartarRef} onClose={onDescClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Descartar e recomeçar?</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Tudo que já foi contado em <Text as="span" fontWeight="semibold">{posicaoParaDescartar}</Text> será
                            apagado e a posição volta do zero.
                            <Text mt={2} fontSize="xs" color="gray.500">
                                O estoque não é alterado — nenhuma revisão foi aplicada.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={confirmDescartarRef} onClick={onDescClose} size="sm">Cancelar</Button>
                            <Button colorScheme="red" onClick={confirmarDescarte} ml={3} size="sm"
                                isLoading={descartando} loadingText="Descartando...">
                                Descartar e recomeçar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default RevisaoEstoque;
